/**
 * QueryPanel — the body of a query inner tab.
 *
 * Layout:
 *   [Saved-queries sidebar]  [Toolbar + Editor + Result panel]
 *
 * Each panel owns:
 *   - one server-side query session (pinned MySQL conn)
 *   - editor text (CodeMirror)
 *   - results array (one entry per executed statement)
 *   - current selected DB + transaction flag (mirrors server state)
 *
 * Props:
 *   connectionId        — the Browser's connection (default target / fallback)
 *   initialConnectionId — persisted per-tab connection (overrides connectionId on restore)
 *   onConnectionChange(connId) — fires when the user switches this tab's connection
 *                                (Shell persists it on the query tab)
 *   connectionName  — display only
 *   initialDatabase — currently selected DB in the connection tab
 *   active          — true when this inner tab is currently visible (for keyboard shortcuts)
 *   onDatabaseChange(db) — optional. Fires whenever the panel's current DB
 *                          changes (dropdown switch OR a USE statement runs).
 *                          Shell uses it to keep the tab title in sync.
 */
import {
  createSignal, createResource, createEffect, createMemo, onCleanup, onMount,
  Show, For, Switch, Match, batch,
} from 'solid-js';
import { createStore } from 'solid-js/store';
import { api } from '../lib/api.js';
import { loadHiddenDatabases } from '../lib/dbvis.js';
import { currentUser } from '../lib/auth-store.js';
import { toast } from '../lib/toast.js';
import {
  createQuerySession, closeQuerySession, closeQuerySessionBeacon,
  useDb, executeQuery, setSessionVars, beginTransaction, commitTransaction,
  rollbackTransaction, cancelQuery, querySessionStatus, updateRow, insertRow, deleteRows,
  keyValues, writeValues, sessionLostInfo, sessionLostMessage, deleteConfirmMessage, deleteNote,
  isTransactionalEngine,
} from '../lib/querysession.js';
import { extractParams, substituteParams, unscopedWriteKind, extractWriteTarget, coerceParam, paramInputType } from '../lib/sqlparams.js';
import { extractStatementName } from '../lib/sqlname.js';
import { buildUpdatePreview, buildInsertPreview, sqlLiteral } from '../lib/sqlpreview.js';
import { loadCompletionSchema, refreshCompletionSchema, completionState, buildSqlSchema, buildRoutineList, isSchemaChangingSQL, implicitCommitKind, isReadOnlySQL, txGuardFunctionNames, keyColumnsFor, aliasForTable } from '../lib/schema.js';
import { focusMode, setFocusMode } from '../lib/focusmode.js';
import {
  listSavedQueries, createSavedQuery, updateSavedQuery, deleteSavedQuery,
} from '../lib/savedqueries.js';
import { listMyHistory } from '../lib/history.js';
import { listScraps, deleteScrap } from '../lib/scraps.js';
import { splitStatements, splitScript, statementAtCursor } from '../lib/sqlsplit.js';
import { expandStatements } from '../lib/sqlexpand.js';
import { outlineItems } from '../lib/sqloutline.js';
import { formatNumberWithSep, relTime } from '../lib/format.js';
import { applyFilterSort, cycleSort } from '../lib/gridview.js';
import { readClipboardPrefill, copyText } from '../lib/export.js';
import {
  querySidebarVisible, setQuerySidebarVisible,
  querySidebarWidth, setQuerySidebarWidth,
  querySoundNotify, setQuerySoundNotify,
  queryNotifyDefault,
  queryAutosaveResults, setQueryAutosaveResults,
  querySafeUpdates, setQuerySafeUpdates, querySafeMode, setQuerySafeMode, queryTxByDefault,
  queryShowThreadId,
  queryEditorSplit, setQueryEditorSplit,
  editorAutoFormat, setEditorAutoFormat,
  editorLineWrap, setEditorLineWrap,
  gridThousandSep, gridShowTypes,
  editInTransaction, editRequireMode,
} from '../lib/prefs.js';
import { playSuccess, playError, primeAudio } from '../lib/sound.js';
import { getQueryNotifyConfig, fireQueryNotify } from '../lib/querynotify.js';
import { setQueryTabRunning } from '../lib/queryruns.js';
import CodeMirrorSQL, { formatEditor } from '../components/CodeMirrorSQL.jsx';
import { registerActiveEditor, wordAtCursor } from '../lib/editorcontext.js';
import ColumnExpandModal from '../components/ColumnExpandModal.jsx';
import Grid from '../components/Grid.jsx';
import ChartView from '../components/ChartView.jsx';
import OptionsMenu from '../components/OptionsMenu.jsx';
import ExportMenu from '../components/ExportMenu.jsx';
import FormatMenu from '../components/FormatMenu.jsx';
import ExplainMenu from '../components/ExplainMenu.jsx';
import RunMenu from '../components/RunMenu.jsx';
import ExplainTree from '../components/ExplainTree.jsx';
import QueryProfiler from '../components/QueryProfiler.jsx';
import FindUsagesModal from '../components/FindUsagesModal.jsx';
import EditorHistoryModal from '../components/EditorHistoryModal.jsx';
import { addEditorVersion } from '../lib/editorhistory.js';
import { saveResult, AUTOSAVE_ROW_CAP } from '../lib/resulthistory.js';
import PKEditConfirmModal from '../components/PKEditConfirmModal.jsx';
import PasteTemplateModal from '../components/PasteTemplateModal.jsx';
import { identAt } from '../lib/findusages.js';
import { commaPasteMenuItems } from '../lib/pastecomma.js';
import ContextMenu from '../components/ContextMenu.jsx';
import Modal from '../components/Modal.jsx';
import QueryDslHelp from '../components/QueryDslHelp.jsx';
import FilterPopover, { formatOp, FILTER_OPS } from '../components/FilterPopover.jsx';
import MessageRow, { copyMessage, copyError } from '../components/MessageRow.jsx';
import { errorText } from '../lib/messagecopy.js';

// Shared, session-cached fetch of all visible connections — so restoring N
// query tabs fires ONE GET /api/connections, not N. Reset on failure so a later
// mount can retry. Session-long staleness is fine for the connection switcher
// (a connection added mid-session shows up after a reload).
let allConnsPromise = null;
function fetchAllConnections() {
  if (!allConnsPromise) {
    allConnsPromise = api.get('/api/connections')
      .then((r) => r.connections || [])
      .catch(() => { allConnsPromise = null; return []; });
  }
  return allConnsPromise;
}

export default function QueryPanel(props) {
  // ── Session lifecycle ─────────────────────────────────────────────────────
  const [sessionId, setSessionId]   = createSignal(null);
  const [threadId,  setThreadId]    = createSignal(null);
  const [currentDb, setCurrentDb]   = createSignal(props.initialDatabase || '');
  // Server identity + capability flags, populated by session-create. Used by
  // ExplainMenu (to disable items the server can't run) and the toolbar chip.
  // null until the first session opens; falsy capabilities → menu items
  // assume "supported" by default (we don't want to silently hide features
  // when detection failed for some transient reason).
  const [serverInfo, setServerInfo] = createSignal(null);
  // The connection this query tab runs against. Seeded from the persisted
  // per-tab connection (workspace) or the Browser's connection, but switchable
  // at runtime to any connection in the same group (see switchConnection).
  const [connId, setConnId]         = createSignal(props.initialConnectionId || props.connectionId);
  const [inTx,      setInTx]        = createSignal(false);
  const [txToggle,  setTxToggle]    = createSignal(queryTxByDefault());  // user wants tx on/off (seeded from pref)
  const [running,   setRunning]     = createSignal(false);

  // Publish run state so the inner tab bar can show a spinner on this tab.
  createEffect(() => setQueryTabRunning(props.tabId, running()));
  onCleanup(() => setQueryTabRunning(props.tabId, false));

  // Editor + selection (CodeMirror exposes EditorView via onViewReady).
  // Seeded from the persisted workspace snapshot so reload restores the content.
  const [sqlText, setSqlText] = createSignal(props.initialSql || '');
  let editorView = null;

  // Ctrl+Shift+E column-expander request (set by the editor, drives the modal).
  const [expandReq, setExpandReq] = createSignal(null);

  // Results-only ("compact") mode — set when this tab is shown in a dual-pane
  // slot with results-only on: hide the sidebar / toolbar / editor, show just
  // the result panel. It's a display mode on the live tab (same session).
  const compact = () => !!props.compact;

  // Report editor content upward (debounced) so the workspace snapshot persists
  // it. The no-op guard in Shell's updateQueryTabContent absorbs the initial
  // restore value, so this won't dirty the snapshot just by mounting.
  let contentTimer = null;
  createEffect(() => {
    const text = sqlText();
    if (!props.onContentChange) return;
    if (contentTimer) clearTimeout(contentTimer);
    contentTimer = setTimeout(() => props.onContentChange(text), 500);
  });
  onCleanup(() => { if (contentTimer) clearTimeout(contentTimer); });

  // Editor local history — snapshot the buffer after an idle pause (longer than
  // the workspace debounce, so versions mark meaningful edits, not keystrokes).
  // The server dedupes a no-change snapshot and prunes old ones; keyed by tabId
  // (the inner id, persisted in the workspace snapshot → survives a reload).
  const [historyOpen, setHistoryOpen] = createSignal(false);
  // 📖 DSL — the query-DSL/editor-extras help modal (expand, :params, snippets).
  const [dslHelpOpen, setDslHelpOpen] = createSignal(false);
  let historyTimer = null;
  createEffect(() => {
    const text = sqlText();
    if (!props.tabId) return;
    if (historyTimer) clearTimeout(historyTimer);
    if (!text.trim()) return;
    historyTimer = setTimeout(() => {
      addEditorVersion({
        tabKey: props.tabId, connectionId: connId() || 0,
        database: currentDb() || '', title: props.title || '', sqlText: text,
      }).catch(() => {});
    }, 3500);
  });
  onCleanup(() => { if (historyTimer) clearTimeout(historyTimer); });

  // Result tabs: a Messages tab (always present, pinned implicitly) plus zero
  // or more Result tabs. Each result tab is one snapshot of a returned grid
  // and can be PINNED, which means the next executed query will NOT overwrite
  // it — instead a new result tab is created. Unpinned tabs are reused so the
  // common case (run query → see result → run another) stays clean.
  //
  // Tab shape: { id, label, columns, rows, truncated, durationMs, pinned }
  // activeResultTab: 'messages' or a tab id.
  // Seeded from the persisted workspace snapshot so a reload restores the run
  // log. Any statement still 'running' when the page was unloaded becomes
  // 'interrupted' (it can't be live in THIS fresh session); on mount we ask the
  // backend about the previous session and relabel honestly (still running /
  // tx left open / closed). Their ids are collected for that relabel.
  const recoverIds = [];
  const [messages, setMessages]   = createStore(
    (props.initialMessages || []).map((m) => {
      if (m.status === 'running') {
        recoverIds.push(m.id);
        return { ...m, status: 'interrupted', result: 'Interrupted — checking previous session…' };
      }
      return m;
    })
  );
  const [resultTabs, setResultTabs] = createStore([]);
  const [activeResultTab, setActiveResultTab] = createSignal('messages');
  let resultTabSeq = 0;
  // Result tabs currently showing a chart instead of the data grid (by tab id).
  const [chartTabs, setChartTabs] = createSignal(new Set());
  const isCharting = (id) => chartTabs().has(id);
  const toggleChart = (id) => {
    const n = new Set(chartTabs());
    n.has(id) ? n.delete(id) : n.add(id);
    setChartTabs(n);
  };

  // ── Completion notification (admin-global feature; per-tab opt-in) ──────────
  // The feature flag (and which targets fire) is admin-set; here we only learn
  // IF it's enabled (to show the Options toggle) and keep a per-tab on/off seeded
  // from the per-user default. Firing happens server-side at run completion.
  const [notifyCfg] = createResource(getQueryNotifyConfig);
  const notifyAvailable = () => !!notifyCfg()?.enabled;
  const [notifyOnComplete, setNotifyOnComplete] = createSignal(queryNotifyDefault());

  // ── Editable result grid ──────────────────────────────────────────────────
  // A single-table SELECT whose result carries `editable` metadata (table + PK,
  // detected server-side) can be edited inline, transaction-wrapped, like the
  // Browser data grid. editResultTab is the id of the result tab currently in
  // edit mode (null = none). Edits go through the reused /update-row endpoint and
  // echo into the message log as viewable SQL.
  const [editResultTab, setEditResultTab] = createSignal(null);
  let flushResultGrid = null;
  const registerResultGridFlusher = (fn) => { flushResultGrid = fn; };
  const editing = () => editResultTab() !== null;

  // New-row insert (Phase 2): on-error mode + the Grid's draft controls. INSERT
  // is offered only when the result is `insertable` (all required real columns
  // present + editable); read-only expression/alias columns stay read-only.
  const [editError, setEditError] = createSignal('stop');
  let rowInserter = null, flushDraftsFn = null, clearDraftsFn = null, clearDirtyFn = null;
  const registerResultRowInserter   = (fn) => { rowInserter = fn; };
  const registerResultInsertFlusher = (fn) => { flushDraftsFn = fn; };
  const registerResultDraftClearer  = (fn) => { clearDraftsFn = fn; };
  const registerResultDirtyClearer  = (fn) => { clearDirtyFn = fn; };
  const [resultDirtyCount, setResultDirtyCount] = createSignal(0);
  const currentEditTab = () => resultTabs.find((t) => t.id === editResultTab());
  const insertableTab = () => !!currentEditTab()?.editable?.insertable;

  // Gutter row-selection in the editable result grid. Indices are absolute
  // positions in gridRows() (the rendered/filtered view); we resolve each to
  // raw rows via PK lookup at delete time. Selection persists across local
  // sort/filter because the Grid stores it by PK (resultRowKeyOf below).
  const [resultSelectedRowIdxs, setResultSelectedRowIdxs] = createSignal([]);
  let clearResultGridRowSelection = null;
  const registerResultRowSelectionClearer = (fn) => { clearResultGridRowSelection = fn; };
  // gridRows()[idx] is the displayed row for `idx` — PK is derived from it.
  // Returns null when there is no editable / no PK / no row at that idx.
  const resultRowKeyOf = (rowIdx) => {
    const tab = currentEditTab();
    if (!tab?.editable?.pkColumns?.length) return null;
    const row = gridRows()[rowIdx];
    if (!row) return null;
    const parts = tab.editable.pkColumns.map((p) => {
      const ci = colIdx(tab, p);
      return ci >= 0 ? String(row[ci]) : '';
    });
    return parts.join('\x1f');
  };

  // PK/identity helpers for editing. The Grid is fed the DERIVED view rows
  // (gridRows), so a Grid rowIdx indexes the *displayed* (possibly
  // filtered/sorted) order — NOT the raw tab.rows. We therefore read the PK from
  // the displayed row and locate the raw row by PK match for the optimistic
  // local update. This keeps editing correct while filter/sort are active
  // (TablePlus/DBeaver behaviour) instead of mis-targeting by stale position.
  // Column lookup by name: exact first, then case-insensitive — MySQL column
  // names are case-insensitive, and a result column is named as the user typed
  // it (`SELECT ID …` for the real `id` the PK list names). A miss here used to
  // read row[-1] → `WHERE id = NULL`, with the edit shown on row 0.
  const colIdx = (tab, name) => {
    const i = tab.columns.findIndex((c) => c.name === name);
    if (i >= 0) return i;
    const low = String(name).toLowerCase();
    return tab.columns.findIndex((c) => String(c.name).toLowerCase() === low);
  };
  const colType = (tab, name) => tab.columns[colIdx(tab, name)]?.type;
  const pkValuesFrom = (tab, row) => tab.editable.pkColumns.map((p) => row[colIdx(tab, p)]);
  // PK values as sent to the backend: integers beyond 2^53 travel exactly.
  const pkWireValues = (tab, vals) => keyValues(vals, tab.editable.pkColumns.map((p) => colType(tab, p)));
  const rawIndexByPk = (tab, pkVals) => {
    const pkIdx = tab.editable.pkColumns.map((p) => colIdx(tab, p));
    return tab.rows.findIndex((r) => pkIdx.every((ci, k) => String(r[ci]) === String(pkVals[k])));
  };

  // Live "would-run" preview while editing a row — UPDATE for an existing row,
  // INSERT for a draft (new) row — updated as the user types / moves between
  // columns, before the row is sent. The empty string hides the bar.
  const [editPreview, setEditPreview] = createSignal('');
  const [showPreviewModal, setShowPreviewModal] = createSignal(false);
  // Pending new-row INSERTs (all drafts), shown as their own list so they're
  // visibly batched under the transaction; the focused-row preview is UPDATE-only.
  const [pendingInserts, setPendingInserts] = createSignal([]);
  const handleResultDraftsChange = (list) => {
    const ed = currentEditTab()?.editable;
    if (!ed) { setPendingInserts([]); return; }
    setPendingInserts((list || []).map((d) => ({
      key: d.key,
      sql: buildInsertPreview({ database: ed.database, table: ed.table, columns: d.values }),
    })));
  };
  const handleResultEditPreview = (rowIdx, changes, kind, meta) => {
    const tab = currentEditTab();
    if (!tab?.editable || rowIdx == null || !changes || Object.keys(changes).length === 0) {
      setEditPreview(''); return;
    }
    const ed = tab.editable;
    if (kind === 'insert') { setEditPreview(''); return; } // shown in the pending-inserts list
    const row = meta?.row || gridRows()[rowIdx]; // the row being edited → correct PK
    if (!row) { setEditPreview(''); return; }
    const pkVals = pkValuesFrom(tab, row);
    setEditPreview(buildUpdatePreview({
      database: ed.database, table: ed.table,
      pkColumns: ed.pkColumns, pkValues: pkVals, setColumns: changes,
    }));
  };

  // Grid → us, per draft, at commit-flush: send the INSERT, return the verdict.
  const handleResultRowInsert = async (values) => {
    const sid = sessionId();
    const tab = currentEditTab();
    // Allowed whenever editing (session + editable tab); no transaction is required
    // in auto-commit mode, where each INSERT commits on its own.
    if (!sid || !tab?.editable) return 'aborted';
    const ed = tab.editable;
    const msgId = pushStatement(`INSERT INTO \`${ed.database}\`.\`${ed.table}\` …`);
    try {
      const r = await editCall((s) => insertRow(s, {
        database: ed.database, table: ed.table,
        columns: writeValues(values, (c) => colType(tab, c)), onError: editError(),
      }), 'new row');
      if (r.error) {
        updateStatement(msgId, { status: 'error', sql: r.sql, result: `Error: ${r.error}`, warnings: r.warnings });
        if (r.autoRolledBack) { setInTx(false); return 'aborted'; }
        return 'skipped';
      }
      updateStatement(msgId, { status: 'ok', sql: r.sql, result: `OK — ${r.rowsAffected} row${r.rowsAffected === 1 ? '' : 's'} inserted`, warnings: r.warnings });
      setInTx(!!r.inTransaction);
      return 'ok';
    } catch (e) {
      updateStatement(msgId, { status: 'error', result: e.message });
      if (await handleSessionLost(e, 'new row')) return 'aborted';
      if (e?.status === 404) { handleResultEditSessionLost(); return 'aborted'; }
      return editError() === 'skip' ? 'skipped' : 'aborted';
    }
  };

  // Switching the visible result tab unmounts the active grid. While editing,
  // flush any pending row first so a buffered edit isn't lost on unmount.
  const switchResultTab = async (id) => {
    if (id === activeResultTab()) return;
    if (editing() && id !== editResultTab() && flushResultGrid) {
      try { await flushResultGrid(); } catch (_) {}
    }
    setActiveResultTab(id);
  };

  // Ephemeral per-result-tab column widths. Query results aren't a persisted
  // table, so there's no colprefs — these live only for the session, keyed by
  // result-tab id. Populated by manual resize + the one-shot Auto-fit button
  // (the Grid registers its measurer via registerAutoFit).
  const [resultWidths, setResultWidths] = createSignal({});
  let gridAutoFit = null;
  const registerGridAutoFit = (fn) => { gridAutoFit = fn; };
  const setTabWidths = (patch) => {
    const tid = activeResultTab();
    if (tid === 'messages' || !patch) return;
    setResultWidths((m) => ({ ...m, [tid]: { ...(m[tid] || {}), ...patch } }));
  };
  const handleAutoFit = () => { if (gridAutoFit) setTabWidths(gridAutoFit()); };
  const handleResultResize = (col, w) => setTabWidths({ [col]: w });

  // ── Autocomplete schema ───────────────────────────────────────────────────
  // Load the connection's cached completion schema (shared across tabs) and
  // build the lang-sql namespace for the editor; defaultSchema = current DB so
  // unqualified `table.col` resolves while `otherdb.table.col` still works.
  createEffect(() => { const id = connId(); if (id) loadCompletionSchema(id); });
  const sqlSchema = createMemo(() => {
    const id = connId();
    if (!id) return null;
    const st = completionState(id)();
    return {
      schema: buildSqlSchema(st.databases),
      defaultSchema: currentDb() || undefined,
      routines: buildRoutineList(st.databases, currentDb()),
      // Raw payload + load state for the FK-join completion + the linter (they
      // need FK edges + a warm-cache gate, which buildSqlSchema's namespace drops).
      databases: st.databases,
      loaded: st.loaded,
      building: st.building,
    };
  });
  // Tx-guard function names — functions NOT flagged read-safe (SQL DATA ACCESS
  // READS SQL DATA / NO SQL). A read calling one keeps the transaction (it might
  // modify data). Recomputes when the schema loads or a flag changes.
  const knownFunctionNames = createMemo(() => {
    const id = connId();
    return id ? txGuardFunctionNames(completionState(id)().databases) : new Set();
  });

  // "Sync schema" action (now in the Options menu). Refreshes the autocomplete
  // cache for the current DB and toasts the outcome.
  const [schemaSyncing, setSchemaSyncing] = createSignal(false);
  const syncSchema = async () => {
    const id = connId();
    if (!id || schemaSyncing()) return;
    setSchemaSyncing(true);
    try {
      await refreshCompletionSchema(id, { database: currentDb() });
      toast.success(`Schema synced${currentDb() ? ` · ${currentDb()}` : ''}`);
    } catch (e) {
      toast.error('Schema sync failed: ' + e.message);
    } finally {
      setSchemaSyncing(false);
    }
  };

  // Persist the message log upward (debounced, capped at 200) so it survives a
  // reload via the workspace snapshot — exactly like the editor content. The
  // first run only registers reactivity (the seed is already persisted); real
  // changes echo up afterwards.
  let msgTimer = null;
  let msgReported = false;
  createEffect(() => {
    const snapshot = messages.map((m) => ({ ...m })); // deep-read so edits are tracked
    if (!props.onMessagesChange) return;
    if (!msgReported) { msgReported = true; return; }
    if (msgTimer) clearTimeout(msgTimer);
    msgTimer = setTimeout(() => props.onMessagesChange(snapshot.slice(-200)), 500);
  });
  onCleanup(() => { if (msgTimer) clearTimeout(msgTimer); });

  // Saved queries
  const [savedList, { refetch: refetchSaved }] = createResource(listSavedQueries);
  const [savedFilter, setSavedFilter] = createSignal('');
  const [historyFilter, setHistoryFilter] = createSignal('');

  // Sidebar mode: saved snippets vs recall history (my query-tab runs).
  const [sidebarTab, setSidebarTab] = createSignal('saved');
  const [historyList, { refetch: refetchHistory }] = createResource(() => listMyHistory(60));
  const loadHistory = (h) => requestLoad(h.sqlText, 'history entry');

  // Scraps — content of closed query tabs (7-day recovery). Fetched when the
  // Scrap tab is opened. Restore opens a NEW tab (no clobber of current work).
  const [scrapList, { refetch: refetchScraps }] = createResource(
    () => (sidebarTab() === 'scrap' ? 'go' : false), listScraps);
  const loadScrap = (sc) => props.onOpenQueryWith?.(sc.database || currentDb(), sc.sqlText, false);

  // Outline — a navigable list of the editor's statements (label + offset). Pure
  // derivation from the current buffer; clicking jumps the caret + scrolls there.
  const outline = createMemo(() => outlineItems(sqlText()));
  const jumpToOutline = (item) => {
    if (!editorView) return;
    editorView.dispatch({ selection: { anchor: item.start }, scrollIntoView: true });
    editorView.focus();
  };
  const removeScrap = async (sc) => { try { await deleteScrap(sc.id); refetchScraps(); } catch (_) {} };

  // All visible connections (for the in-tab connection switcher), via the
  // shared session cache; the same-group subset drives the picker.
  const [allConns] = createResource(fetchAllConnections);
  const sameGroupConns = createMemo(() => {
    const list = allConns() || [];
    const me = list.find((c) => c.id === connId());
    if (!me) return [];
    const g = me.group || '';
    return list.filter((c) => (c.group || '') === g);
  });
  // The connections the in-tab picker offers. Normally scoped to the current
  // connection's group (keeps the dropdown short + on-task); in distraction-free
  // focus mode the sidebar is hidden, so we widen it to EVERY connection — that
  // dropdown becomes the only way to hop connections without leaving focus mode.
  // (switchConnection already allows any target; this only widens the UI list.)
  const pickerConns = createMemo(() => focusMode() ? (allConns() || []) : sameGroupConns());

  // Database list for the dropdown — sorted by recent use
  const [databases] = createResource(
    () => connId(),
    async (id) => {
      try {
        const r = await api.get(`/api/connections/${id}/databases`);
        const list = r.databases || [];
        // Sort: ones with lastUsed > 0 by lastUsed desc, then alpha for the rest
        const used   = list.filter((d) => d.lastUsed > 0)
                            .sort((a, b) => b.lastUsed - a.lastUsed);
        const unused = list.filter((d) => !d.lastUsed)
                            .sort((a, b) => a.name.localeCompare(b.name));
        return [...used, ...unused];
      } catch { return []; }
    }
  );

  // Honor the sidebar's per-(user,connection) hidden-database list so the DB
  // dropdown matches the tree. The currently-selected DB is always kept (so the
  // dropdown can show it) even if it's hidden.
  const [hiddenDbs] = createResource(() => connId(), (id) => loadHiddenDatabases(id));
  const hiddenSet = createMemo(() => new Set(hiddenDbs() ?? []));
  const visibleDatabases = createMemo(() => {
    const hide = hiddenSet();
    const cur = currentDb();
    return (databases() ?? []).filter((d) => !hide.has(d.name) || d.name === cur);
  });

  // (Re)open a pinned session on the tab's current connection + DB and adopt it.
  // Shared by mount and the auto-reconnect path. Returns the new session id.
  const openSession = async () => {
    const r = await createQuerySession(connId(), currentDb() || null);
    batch(() => {
      setSessionId(r.sessionId);
      setThreadId(r.threadId);
      setCurrentDb(r.database || currentDb());
      setInTx(!!r.inTransaction);
      if (r.serverInfo) setServerInfo(r.serverInfo);
    });
    // Persist the new session id so a future reload can check THIS session.
    props.onSessionIdChange?.(r.sessionId);
    return r.sessionId;
  };

  // Run a session-bound call; if the session was reaped while idle (the backend
  // returns HTTP 404 "session not found"), transparently reopen on the same
  // connection + DB and retry ONCE. Safe because: a 404 from resolveSession means
  // the statement never reached MySQL (nothing ran), and the idle reaper never
  // evicts an in-transaction session — so a reaped session never had uncommitted
  // work. Caveat (surfaced in the log): ad-hoc session state from earlier runs
  // (a manual `SET @v=…`, temp tables) does NOT survive the fresh connection;
  // query `:params` are re-bound each run so they're unaffected.
  const withReconnect = async (action, what = 'statement') => {
    try {
      return await action(sessionId());
    } catch (e) {
      if (await handleSessionLost(e, what)) throw e;
      if (e?.status !== 404) throw e;
      if (inTx()) {
        // The session vanished while we believed a transaction was open (it was
        // killed from the Sessions panel): the transaction is gone, and a silent
        // retry would run this statement OUTSIDE it, auto-committed.
        const msg = 'The session was closed while a transaction was open — the server rolled it back; its uncommitted changes are gone. Nothing was retried.';
        logError(msg);
        toast.error(msg);
        abandonEditMode();
        setInTx(false);
        await openSession();
        throw e;
      }
      logInfo('Session had expired while idle — reconnected on a fresh connection.');
      await openSession();
      return await action(sessionId());
    }
  };

  // Leave result-grid edit mode without touching the server (its session or
  // transaction is already gone) and re-read the result.
  const abandonEditMode = () => {
    const editTab = editResultTab();
    if (!editTab) return;
    setEditResultTab(null);
    setEditPreview('');
    clearDraftsFn?.();
    clearDirtyFn?.();
    queueMicrotask(() => refreshResultTab(editTab));
  };

  // A 410 from the backend: the session's connection died with something at
  // stake — an open transaction (rolled back by the server), a changed
  // autocommit, or a statement whose outcome is unknown. Tell the user exactly
  // that, leave edit mode, and open a fresh session so the tab keeps working —
  // but never retry. Returns true if `e` was such a reply (idempotent per error).
  const handleSessionLost = async (e, what = 'statement') => {
    const info = sessionLostInfo(e);
    if (!info) return false;
    if (e.lostHandled) return true;
    e.lostHandled = true;
    const msg = sessionLostMessage(info, what);
    e.message = msg;
    logError(msg);
    toast.error(msg);
    setInTx(false);
    try {
      await openSession();
      logInfo(`Reconnected on a fresh connection (thread ${threadId()}).`);
    } catch (err) {
      logError('Reconnect failed: ' + err.message);
    }
    abandonEditMode();
    return true;
  };

  // Session-bound edit-grid call. In auto-commit edit mode nothing is held
  // between writes, so a session reaped while idle (404) is reopened and the
  // write retried transparently; inside a transaction that would move the write
  // out of it, so there a 404 is left to the caller (it ends edit mode).
  const editCall = (fn, what) => (editInTransaction() ? fn(sessionId()) : withReconnect(fn, what));

  // ── Mount: create session ─────────────────────────────────────────────────
  onMount(async () => {
    try {
      await openSession();
      logInfo(`Session opened on connection (thread ${threadId()})`);
      // Freshly opened from "Fill into new tab & run" — run the seeded query once.
      // (The flag isn't persisted, so a reload won't re-run; see Shell.)
      if (props.initialAutoRun) queueMicrotask(() => runAll());
    } catch (e) {
      toast.error('Failed to open query session: ' + e.message);
      logError(e.message);
    }

    // Honestly relabel any statement that was in flight when the page unloaded,
    // by asking the backend about the PREVIOUS session (read-only).
    if (recoverIds.length > 0) {
      const relabel = (result) => { for (const id of recoverIds) updateStatement(id, { status: 'interrupted', result }); };
      const prev = props.initialSessionId;
      if (!prev) {
        relabel('Interrupted — page reloaded. Verify the outcome in the History tab.');
      } else {
        try {
          const st = await querySessionStatus(prev);
          if (!st.exists) {
            relabel('Interrupted — the previous session was closed (any open transaction rolled back). Verify in History.');
          } else if (st.running) {
            relabel('Still running on the backend — it will finish on the server. Check History for the outcome, or kill it in the Sessions tab to abort.');
          } else if (st.inTransaction) {
            relabel('Finished, but its transaction is still OPEN on the backend (uncommitted). Kill it in the Sessions tab to roll it back and release locks.');
          } else {
            relabel('Completed after the reload. Verify the outcome in the History tab.');
          }
        } catch {
          relabel('Interrupted — could not verify the previous session. Check History.');
        }
      }
    }

    // Guard against an accidental refresh/close/navigation while a query is
    // running or a transaction is open — the native browser prompt lets the
    // user cancel it. (Browsers ignore custom text and only prompt for
    // user-initiated unloads, not crashes/kills — those stay covered by the
    // orphan reaper + the Sessions/Transactions panels.)
    const onBeforeUnload = (e) => {
      if (running() || inTx()) {
        e.preventDefault();
        e.returnValue = ''; // required by some browsers to trigger the prompt
      }
    };
    // Clean up the pinned session on an ACTUAL unload. pagehide fires only after
    // the user confirms leaving, so choosing "Stay" never closes the session.
    const onPageHide = () => {
      const sid = sessionId();
      if (sid) closeQuerySessionBeacon(sid);
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    window.addEventListener('pagehide', onPageHide);
    onCleanup(() => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('pagehide', onPageHide);
    });
  });

  onCleanup(() => {
    const sid = sessionId();
    if (sid) closeQuerySession(sid);
  });

  // Notify the parent whenever the current DB changes — covers both the
  // dropdown (switchDb) and `USE foo` statements (executeStatement returns
  // the new DB in the result, execOne forwards it into setCurrentDb).
  // Skip the very first run so we don't echo the initial-prop value back.
  let lastReportedDb = props.initialDatabase || '';
  createEffect(() => {
    const db = currentDb();
    if (db === lastReportedDb) return;
    lastReportedDb = db;
    props.onDatabaseChange?.(db);
  });

  // ── Keyboard shortcuts (Ctrl+R, Ctrl+Shift+R) ─────────────────────────────
  // Navicat-style: Ctrl+R runs everything (or the selection if any); Ctrl+Shift+R
  // runs the statement under the cursor (or the selection if any).
  // rootEl scopes the shortcut to the panel actually on screen. Query tabs are
  // kept mounted (hidden via display:none), and a panel in a NON-focused outer
  // tab can still be its connection's "active" inner tab — so props.active alone
  // would let a hidden tab grab Ctrl+R (or none would, and the browser reloads).
  // offsetParent === null means this panel (or an ancestor) is display:none.
  let rootEl;
  const onKey = (e) => {
    const ctrl = e.ctrlKey || e.metaKey;
    if (!ctrl || (e.key !== 'r' && e.key !== 'R')) return;
    if (!props.active || !rootEl || rootEl.offsetParent === null) return; // not the visible panel
    e.preventDefault();
    if (e.shiftKey) runCurrentOrSelection();
    else            runAll();
  };
  onMount(() => {
    document.addEventListener('keydown', onKey);
    onCleanup(() => document.removeEventListener('keydown', onKey));
  });

  // ── Helpers ───────────────────────────────────────────────────────────────
  const logInfo  = (text) => setMessages((m) => [...m, { kind: 'info',  text, at: Date.now() }]);
  const logError = (text) => setMessages((m) => [...m, { kind: 'error', text, at: Date.now() }]);
  const logOK    = (text, meta) => setMessages((m) => [...m, { kind: 'ok', text, at: Date.now(), ...meta }]);

  // Per-statement messages: echo the SQL (status 'running'), then fill in the
  // result so each outcome maps to its query (and you can see which one is live
  // or got aborted). `nowTick` drives the running statement's elapsed timer.
  // Continue past any restored statement IDs so new statements never collide
  // with persisted ones (else updateStatement would patch the wrong row).
  let msgSeq = (props.initialMessages || []).reduce((mx, m) => (m.id > mx ? m.id : mx), 0);
  const [nowTick, setNowTick] = createSignal(Date.now());
  createEffect(() => {
    if (!running()) return;
    const h = setInterval(() => setNowTick(Date.now()), 250);
    onCleanup(() => clearInterval(h));
  });
  // isExplainSyntaxUnsupported — true when the server rejected an EXPLAIN
  // variant we asked for (FORMAT=JSON or ANALYZE) with the 1064 parser error,
  // pointing at `=JSON` / `ANALYZE`. Used to provide a friendly explanation
  // (and auto-fallback) instead of dumping a raw MySQL syntax error on the
  // user — typical on MariaDB ≤ 10.0 and MySQL ≤ 5.5, where these forms
  // simply don't exist.
  const isExplainSyntaxUnsupported = (errMsg) => {
    if (!errMsg) return false;
    const m = errMsg.toLowerCase();
    if (!m.includes('1064')) return false;
    return m.includes('=json') || m.includes('format=json') || m.includes('analyze');
  };

  // pushStatement(sql, name?) — optional name shows up as a small chip on the
  // message row so a glance down the log identifies each run without re-reading
  // the SQL. Comes from the `~name~` / `-- @name:` annotation extracted by
  // runWithParams; null/undefined when the user didn't name it.
  const pushStatement = (sql, name) => {
    const id = ++msgSeq;
    setMessages((m) => [...m, { id, kind: 'statement', sql, name: name || undefined, status: 'running', at: Date.now() }]);
    return id;
  };
  const updateStatement = (id, patch) => {
    const idx = messages.findIndex((m) => m.id === id);
    if (idx >= 0) setMessages(idx, (m) => ({ ...m, ...patch }));
  };

  // Single-statement execution (used by both runAtCursor and runAll loop).
  // safeUpdates toggles MySQL SQL_SAFE_UPDATES for this statement (false only
  // for a write the user confirmed via the no-WHERE override).
  // explain (null | 'json' | 'analyze') routes the result to a dedicated plan
  // view instead of the data grid: 'json' parses EXPLAIN FORMAT=JSON into a tree,
  // 'analyze' shows EXPLAIN ANALYZE's tree-text. 'plan' (tabular EXPLAIN) is left
  // null so it renders as an ordinary grid result.
  // labelOverride (5th arg) — when a statement was prefixed with `~name~`,
  // this is the captured name. We pass it down to publishResult so the result
  // tab is titled with the user's choice instead of the default "Result #N"
  // / "Plan (JSON)" / "Analyze". Multi-result-set statements (a procedure
  // returning N sets) suffix the index — "Get Foo", "Get Foo (2)", …
  const execOne = async (stmt, safeUpdates = true, explain = null, wrapTx = true, labelOverride = null) => {
    if (!sessionId()) { logError('No active session.'); return false; }

    // Auto-BEGIN if user toggled transactions on and we're not in one yet — unless
    // this run is read-only (wrapTx=false), where a transaction would only leave a
    // dangling open snapshot with nothing to commit.
    if (txToggle() && !inTx() && wrapTx) {
      try {
        await withReconnect((sid) => beginTransaction(sid));
        setInTx(true);
        logInfo('BEGIN');
      } catch (e) {
        logError('BEGIN failed: ' + e.message);
        return false;
      }
    }

    const msgId = pushStatement(stmt, labelOverride);
    try {
      const r = await withReconnect((sid) => executeQuery(sid, stmt, safeUpdates));
      setCurrentDb(r.database || currentDb());
      setInTx(!!r.inTransaction);
      if (r.implicitCommit) {
        logInfo('COMMIT — the open transaction was committed by this statement (MySQL commits before DDL, LOCK TABLES and admin statements, and when autocommit is switched back on).');
      }
      if (r.autoRolledBack) {
        updateStatement(msgId, { status: 'error', result: `Failed: ${r.error}`, durationMs: r.durationMs });
        logInfo('Transaction auto-ROLLBACK on error');
        return false;
      }
      if (r.error) {
        // Friendly handling for the very common "old MariaDB / MySQL 5.5
        // rejects FORMAT=JSON / ANALYZE" parse error. The cryptic 1064 next
        // to "=JSON" or "ANALYZE" is unhelpful — replace it with a real
        // explanation and (for the JSON tree mode) auto-fall-back to plain
        // tabular EXPLAIN, since that's the answer the user wanted anyway.
        if (explain === 'json' && isExplainSyntaxUnsupported(r.error)) {
          updateStatement(msgId, {
            status: 'error',
            result: 'Server doesn\'t support EXPLAIN FORMAT=JSON — needs MariaDB 10.1+ or MySQL 5.6+. Falling back to tabular EXPLAIN…',
            durationMs: r.durationMs,
          });
          // Strip the FORMAT=JSON prefix to re-run as plain EXPLAIN. The stmt
          // looks like `EXPLAIN FORMAT=JSON <inner>`; everything after the
          // first `FORMAT=JSON` token is the inner statement.
          const inner = stmt.replace(/^\s*EXPLAIN\s+FORMAT\s*=\s*JSON\s+/i, '');
          if (inner && inner !== stmt) {
            return execOne('EXPLAIN ' + inner, safeUpdates, null /* tabular */, wrapTx, labelOverride);
          }
        }
        if (explain === 'analyze' && isExplainSyntaxUnsupported(r.error)) {
          updateStatement(msgId, {
            status: 'error',
            result: 'Server doesn\'t support EXPLAIN ANALYZE — needs MariaDB 10.1+ or MySQL 8.0.18+. Try "Explain (tabular)" instead.',
            durationMs: r.durationMs,
          });
          return false;
        }
        updateStatement(msgId, { status: 'error', result: `Error: ${r.error}`, durationMs: r.durationMs });
        return false;
      }
      // EXPLAIN FORMAT=JSON / EXPLAIN ANALYZE return a single cell — publish it to
      // a dedicated plan tab (tree / monospace text) rather than a 1×1 grid.
      if (explain === 'json' || explain === 'analyze') {
        const cell = (r.resultSets || [])[0]?.rows?.[0]?.[0];
        const text = cell == null ? '' : String(cell);
        publishResult({
          label: labelOverride || (explain === 'json' ? 'Plan (JSON)' : 'Analyze'),
          columns: [], rows: [], durationMs: r.durationMs, sql: stmt,
          explainJson: explain === 'json' ? text : undefined,
          explainText: explain === 'analyze' ? text : undefined,
        });
        updateStatement(msgId, {
          status: 'ok',
          result: explain === 'json' ? 'Query plan (FORMAT=JSON)' : 'EXPLAIN ANALYZE',
          durationMs: r.durationMs,
          warnings: r.warnings,
        });
        return true;
      }
      const sets = r.resultSets || [];
      if (sets.length > 0) {
        // One tab per result set. Columns are [{name, type}] (type from
        // ColumnTypes.DatabaseTypeName) so the Grid's isNumericType /
        // thousand-separator formatting works for query results too.
        // Multi-result-set (a procedure that returns several) gets suffixed
        // indices when the user named the statement, so "Get Foo (2)" is
        // distinguishable from "Get Foo".
        for (let si = 0; si < sets.length; si++) {
          const rset = sets[si];
          publishResult({
            label: labelOverride
              ? (sets.length > 1 ? `${labelOverride} (${si + 1})` : labelOverride)
              : undefined,
            columns: rset.columns || [],
            rows: rset.rows || [],
            truncated: rset.truncated,
            durationMs: r.durationMs,
            sql: stmt, // so the result can be re-run for export
            editable: rset.editable || null, // {database, table, pkColumns} when single-table+PK
          });
        }
        let summary;
        if (sets.length === 1) {
          const n = (sets[0].rows || []).length;
          summary = `${formatNumberWithSep(n)} row${n === 1 ? '' : 's'} returned${sets[0].truncated ? ' (truncated — add a WHERE/LIMIT or export to see the rest)' : ''}`;
        } else {
          const total = sets.reduce((acc, s) => acc + (s.rows || []).length, 0);
          summary = `${sets.length} result sets — ${total} row${total === 1 ? '' : 's'} total`;
        }
        updateStatement(msgId, { status: 'ok', result: summary, durationMs: r.durationMs, warnings: r.warnings });
      } else {
        // Outside a transaction a write is auto-committed (permanent) — say so.
        const committed = !r.inTransaction && r.rowsAffected > 0 ? ' · committed' : '';
        updateStatement(msgId, {
          status: 'ok',
          // A CALL / EXECUTE with no result set reports no affected-row count.
          result: r.rowsAffectedUnknown
            ? 'OK (no row count reported)'
            : `OK — ${r.rowsAffected} row${r.rowsAffected === 1 ? '' : 's'} affected${r.lastInsertId ? ` (last id ${r.lastInsertId})` : ''}${committed}`,
          durationMs: r.durationMs,
          warnings: r.warnings,
        });
      }
      return true;
    } catch (e) {
      updateStatement(msgId, { status: 'error', result: e.message });
      return false;
    }
  };

  // Source to run: the selection if there is one, else the whole editor.
  const runSource = () => {
    if (editorView) {
      const sel = editorView.state.selection.main;
      if (sel.from !== sel.to) return editorView.state.sliceDoc(sel.from, sel.to);
    }
    return sqlText();
  };

  // Run a list of statements sequentially, stopping at the first error. Scans
  // for `:name` parameters across everything being run; if any, prompts once,
  // binds the values as session variables (SET @name = ?), and runs each
  // statement with `:name` rewritten to `@name`.
  const runWithParams = async (stmts, opts = {}) => {
    if (running()) return;
    if (stmts.length === 0) { logInfo('Nothing to run.'); return; }
    // If the session was never opened (mount failed), try once now; a session
    // reaped while idle is handled lazily by withReconnect on the first call.
    if (!sessionId()) {
      try { await openSession(); }
      catch (e) { logError('No active session and reconnect failed: ' + e.message); return; }
    }
    primeAudio(); // within the click gesture, so the completion sound can play

    const { params } = extractParams(stmts.map((s) => s.text).join('\n;\n'));
    if (params.length > 0) {
      const values = await promptParams(params, stmts.map((s) => s.text).join(';\n'));
      if (values === null) return; // cancelled (or "filled into a new tab" instead)
      try {
        await withReconnect((sid) => setSessionVars(sid, values));
      } catch (e) {
        logError('Failed to set parameters: ' + e.message);
        return;
      }
    }

    // `expand src="…", target="…&Col…"` — runs src (any @params it needs are
    // already resolved via the session vars set just above), then replaces
    // itself with one real statement per src row. Must happen before the
    // safe-update guard below so it analyses the ACTUAL generated statements,
    // not the opaque `expand` wrapper.
    try {
      stmts = await expandStatements(stmts, async (sql) => {
        const r = await withReconnect((sid) => executeQuery(sid, sql, false));
        return { columns: r.resultSets?.[0]?.columns, rows: r.resultSets?.[0]?.rows, error: r.error };
      });
    } catch (e) {
      logError(e.message);
      return;
    }

    // Effective statements (params rewritten), + safe-update mode guards.
    // Per-statement label override — strip the optional `~name~` prefix from
    // each statement; the captured name becomes that result tab's title.
    // Done BEFORE the safe-update parser so the `~name~` doesn't confuse the
    // statement-shape detection.
    const named = stmts.map((s) => extractStatementName(s.text));
    const names = named.map((n) => n.name);
    const cleaned = named.map((n) => n.sql);
    const texts = cleaned.map((t) => (params.length > 0 ? extractParams(t).rewritten : t));
    const mode = querySafeMode(); // 'strict' | 'lax-key' | 'lax-any' | 'off'
    const isStrict = mode === 'strict';
    // Per-statement target info (kind / table / WHERE columns / hasLimit). Reused
    // by both the client-side guard and the server-flag decision below.
    const targets = texts.map(extractWriteTarget);
    let overrideIdxs = null; // Set<i> of statements the user confirmed past the guard

    if (mode !== 'off') {
      // For each write statement, decide if the guard blocks it. LIMIT always
      // passes (it scopes the write). The reason determines the modal message.
      const dbs = completionState(connId())().databases;
      const curDB = currentDb();
      const blocked = [];
      for (let i = 0; i < targets.length; i++) {
        const t = targets[i];
        if (!t.kind) continue;            // not a write
        if (t.hasLimit) continue;         // LIMIT scopes; pass
        if (!t.hasWhere) {
          // Bare UPDATE/DELETE — blocked under strict, lax-any, AND lax-key.
          blocked.push({ i, kind: t.kind, sql: cleaned[i], reason: 'no-where' });
          continue;
        }
        if (mode === 'lax-key') {
          if (!t.table) {
            // Couldn't parse the target — fall through to the server. (Strict
            // would catch it; lax-key/lax-any have no server flag.) Be defensive
            // and warn the user with the modal so they know we couldn't check.
            blocked.push({ i, kind: t.kind, sql: cleaned[i], reason: 'unknown-table' });
            continue;
          }
          const keys = keyColumnsFor(dbs, curDB, t.table);
          if (keys == null) {
            // Schema cache has no entry / pre-keys row — surface as a soft block
            // so the user can refresh schema or override.
            blocked.push({ i, kind: t.kind, sql: cleaned[i], reason: 'no-key-info', table: t.table });
            continue;
          }
          if (keys.size === 0) continue; // table genuinely has zero indexes — not a safety issue, allow
          const hit = t.whereCols.some((c) => keys.has(c));
          if (!hit) {
            blocked.push({ i, kind: t.kind, sql: cleaned[i], reason: 'no-key-where', table: t.table, keys: [...keys] });
          }
        }
        // mode === 'strict' or 'lax-any' with a present WHERE: pass the client
        // check. (Strict mode's server flag will reject if the WHERE doesn't hit
        // an indexed column at the planner's leftmost-prefix rule — that's the
        // whole point of the strict tier.)
      }
      if (blocked.length > 0) {
        const ok = await confirmUnscoped(blocked);
        if (!ok) return; // cancelled — nothing ran
        overrideIdxs = new Set(blocked.map((b) => b.i));
      }
    }

    // Implicit-commit guard: if a transaction is open, warn before any statement
    // that MySQL would implicitly COMMIT it for (DDL, BEGIN/START TRANSACTION,
    // LOCK TABLES, account/maintenance statements, SET autocommit=1). MySQL
    // commits the open transaction *before* such a statement and the commit can't
    // be rolled back — without this the "UNCOMMITTED" badge would silently lie.
    // Gated on inTx only — independent of Safe mode (a separate concern).
    if (inTx()) {
      const commitKinds = texts.map(implicitCommitKind); // parallel to `kinds` above
      const committing = commitKinds
        .map((k, i) => (k ? { kind: k, sql: cleaned[i] } : null))
        .filter(Boolean);
      if (committing.length > 0) {
        const ok = await confirmImplicitCommit(committing);
        if (!ok) return; // cancelled — nothing ran
      }
    }

    setActiveResultTab('messages');
    clearUnpinnedResults();
    const pinnedCount = resultTabs.length; // only pinned tabs remain
    // If EVERY statement in this run only reads, don't auto-open a transaction for
    // it (even with the toggle on) — there'd be nothing to commit, just a dangling
    // open snapshot. A single write anywhere keeps the transaction. No effect when
    // a transaction is already open (reads simply join it).
    const fnNames = knownFunctionNames();
    const runIsReadOnly = texts.length > 0 && texts.every((t) => isReadOnlySQL(t, fnNames));
    if (runIsReadOnly && txToggle() && !inTx()) {
      logInfo('Read-only run — skipped the transaction (nothing to commit).');
    }
    setRunning(true);
    let hadError = false;
    for (let i = 0; i < stmts.length; i++) {
      // Server-side SQL_SAFE_UPDATES is only ON in 'strict' mode, AND only when
      // this statement wasn't user-overridden. Lax modes always send 0 (the
      // client modal was the guard). 'off' is always 0.
      const overridden = overrideIdxs && overrideIdxs.has(i);
      const su = isStrict && !overridden;
      const ok = await execOne(texts[i], su, opts.explain || null, !runIsReadOnly, names[i]);
      if (!ok) { hadError = true; break; }
    }
    setRunning(false);
    focusFirstNewResult(pinnedCount);
    refetchHistory();
    if (querySoundNotify()) (hadError ? playError : playSuccess)();
    // Completion notification → the admin-configured success/failure target.
    if (notifyAvailable() && notifyOnComplete()) {
      const firstLine = (texts[0] || '').replace(/\s+/g, ' ').trim().slice(0, 160);
      const lines = [
        currentDb() ? `Database: ${currentDb()}` : '',
        firstLine ? `SQL: ${firstLine}${texts.length > 1 ? ` (+${texts.length - 1} more)` : ''}` : '',
      ].filter(Boolean);
      fireQueryNotify({
        status: hadError ? 'error' : 'ok',
        title: `Ninocat query ${hadError ? 'failed' : 'succeeded'}`,
        message: lines.join('\n') || '(no details)',
      });
    }
    // A schema-changing statement (CREATE/ALTER/DROP/RENAME/TRUNCATE) ran in
    // this tab → refresh autocomplete for the current DB so new objects show up.
    if (texts.some(isSchemaChangingSQL)) refreshCompletionSchema(connId(), { database: currentDb() });
  };

  const runAtCursor = async () => {
    if (running()) return;
    const cursor = editorView?.state?.selection?.main?.head ?? sqlText().length;
    const stmt = statementAtCursor(sqlText(), cursor);
    if (!stmt) { logInfo('No statement under cursor.'); return; }
    await runWithParams([stmt]);
  };

  // Ctrl+Shift+R / "Run current statement": run the selection if there is one,
  // otherwise just the statement under the cursor. (Ctrl+R runs everything, or
  // the selection.)
  const runCurrentOrSelection = async () => {
    if (running()) return;
    const sel = editorView?.state?.selection?.main;
    if (sel && sel.from !== sel.to) {
      await runWithParams(splitStatements(editorView.state.sliceDoc(sel.from, sel.to)));
      return;
    }
    await runAtCursor();
  };

  // Split on `;` (quote/comment aware).
  const runAll = async () => {
    if (running()) return;
    await runWithParams(splitStatements(runSource()));
  };

  // Honour DELIMITER directives so SP / trigger / function bodies run whole.
  const runScript = async () => {
    if (running()) return;
    await runWithParams(splitScript(runSource()));
  };

  // Run the statement under the cursor through EXPLAIN. mode:
  //   'plan'    → EXPLAIN <stmt>           (tabular grid; every version)
  //   'json'    → EXPLAIN FORMAT=JSON …    (cost/rows tree; 5.6+/MariaDB 10.1+)
  //   'analyze' → EXPLAIN ANALYZE …        (8.0.18+, SELECT only — EXECUTES it)
  // Reuses the normal run pipeline (so :params are prompted/bound and the run is
  // audited); the EXPLAIN-prefixed text is never a write, so the no-WHERE guard
  // and safe-updates checks naturally no-op on the leading EXPLAIN keyword.
  // ── Find usages ─────────────────────────────────────────────────────────
  // Opens FindUsagesModal pre-filled with the identifier under the cursor
  // (or the user's selection if there is one). Ctrl+Shift+U is the trigger;
  // there's also a toolbar button. Modal is rendered conditionally; closing
  // it clears the initial token so a subsequent open starts blank if the
  // user invokes without a fresh selection.
  const [findOpen, setFindOpen] = createSignal(false);
  const [findInitial, setFindInitial] = createSignal('');
  // The Find-Usages open path is shared between the toolbar button and the
  // editor right-click menu — second arg lets the right-click pre-fill an
  // ident it computed from the click coordinates (otherwise the modal would
  // read from the editor's selection / cursor, which the right-click hasn't
  // moved yet).
  const openFindUsages = (preFillIdent) => {
    if (preFillIdent != null) {
      setFindInitial(preFillIdent);
      setFindOpen(true);
      return;
    }
    let ident = '';
    if (editorView) {
      const sel = editorView.state.selection.main;
      if (sel.from !== sel.to) {
        ident = editorView.state.sliceDoc(sel.from, sel.to).trim();
      } else {
        ident = identAt(sqlText(), sel.head);
      }
    }
    setFindInitial(ident);
    setFindOpen(true);
  };

  // ── Editor right-click context menu ─────────────────────────────────────
  // Wires the SQL editor's `contextmenu` DOM event into a small floating
  // menu of Ninocat-specific actions. The menu items are computed at click
  // time so they can be context-aware: "Find usages of <ident>" pre-fills
  // whatever identifier sits under the click, and "Add result title…" knows
  // which statement the click landed inside.
  const [editorCtxMenu, setEditorCtxMenu] = createSignal(null); // { x, y, items }
  // Right-click menu for a message-log row → Copy message / Copy error.
  const [msgCtxMenu, setMsgCtxMenu] = createSignal(null); // { x, y, items }
  const onMessageContextMenu = (e, m) => {
    e.preventDefault();
    setMsgCtxMenu({
      x: e.clientX,
      y: e.clientY,
      items: [
        { label: 'Copy message', icon: '⧉', onClick: () => copyMessage(m) },
        { label: 'Copy error', icon: '⚠', disabled: !errorText(m), onClick: () => copyError(m) },
      ],
    });
  };
  // Title-prompt dialog state: { stmtFrom, stmtTo, existingName, draft }
  const [titlePrompt, setTitlePrompt] = createSignal(null);
  let titleInputRef;

  // Helpers used by the right-click "Format" items. Statement-scoped formats
  // momentarily select the statement so formatEditor's selection-aware path
  // operates on it; whole-doc formats just clear the selection (anchor === head)
  // so it falls through to the "whole document" path. formatEditor restores
  // focus to the editor after each, so the user can keep typing.
  const formatRange = (view, from, to, mode) => {
    view.dispatch({ selection: { anchor: from, head: to } });
    formatEditor(view, mode);
  };
  const formatAll = (view, mode) => {
    const head = view.state.selection.main.head;
    view.dispatch({ selection: { anchor: head, head } });
    formatEditor(view, mode);
  };

  // Resolve a table/view reference at the given doc position from the cached
  // schema. Supports a `db.tbl` qualifier — if the click landed on `tbl` with
  // a preceding `.<schema>`, we look in that schema first; otherwise we try
  // the current DB, then fall back to any DB on this connection that has a
  // table by the same name (so a SELECT against another DB still resolves).
  // Returns { name, columns, database } or null. Case-insensitive on names.
  const resolveTableAt = (text, pos) => {
    const isId = (c) => c === '_' || c === '$' ||
      (c >= '0' && c <= '9') || (c >= 'A' && c <= 'Z') || (c >= 'a' && c <= 'z');
    let start = pos;
    while (start > 0 && isId(text[start - 1])) start--;
    let end = pos;
    while (end < text.length && isId(text[end])) end++;
    if (start === end) return null;
    const name = text.slice(start, end);
    // Optional preceding "<schema>." — walk past it if present.
    let schema = '';
    if (start >= 2 && text[start - 1] === '.') {
      let qEnd = start - 1, qStart = qEnd;
      while (qStart > 0 && isId(text[qStart - 1])) qStart--;
      if (qStart < qEnd) schema = text.slice(qStart, qEnd);
    }
    const cid = connId();
    if (!cid) return null;
    const dbs = completionState(cid)().databases || [];
    const wantLower = name.toLowerCase();
    const findIn = (db) => {
      if (!db) return null;
      const t = (db.tables || []).find((x) => x.name.toLowerCase() === wantLower)
            || (db.views  || []).find((x) => x.name.toLowerCase() === wantLower);
      return t ? { name: t.name, columns: t.columns || [], database: db.database } : null;
    };
    // Schema-qualified: only look in that DB.
    if (schema) {
      const db = dbs.find((d) => d.database.toLowerCase() === schema.toLowerCase());
      return findIn(db);
    }
    // Else: current DB first, then any DB on this connection.
    const curHit = findIn(dbs.find((d) => d.database === currentDb()));
    if (curHit) return curHit;
    for (const db of dbs) {
      const hit = findIn(db);
      if (hit) return hit;
    }
    return null;
  };

  const onEditorContextMenu = (e, view) => {
    if (!view) return;
    // Document position at the click. Falls back to current cursor when the
    // click landed in a corner the layout can't resolve.
    let pos = view.posAtCoords({ x: e.clientX, y: e.clientY });
    if (pos == null) pos = view.state.selection.main.head;

    // Identifier at the clicked position — for "Find usages of <ident>".
    // If there's an active selection that covers the click, prefer that.
    const sel = view.state.selection.main;
    const selCovers = sel.from !== sel.to && pos >= sel.from && pos <= sel.to;
    const ident = selCovers
      ? view.state.sliceDoc(sel.from, sel.to).trim()
      : identAt(sqlText(), pos);

    // Statement at the clicked position — for "Add result title…" + scoped format.
    const stmt = statementAtCursor(sqlText(), pos);
    // "On whitespace?" — the heuristic the format items use to decide whether
    // to offer statement-scoped variants. If the click landed on a blank line
    // (only whitespace before EOL), treat it as the user gesturing "format the
    // whole script", not "format this statement I'm hovering over."
    const lineText = view.state.doc.lineAt(pos).text;
    const onWhitespace = !lineText.trim();

    const items = [];
    if (stmt) {
      const existing = extractStatementName(stmt.text);
      items.push({
        label: existing.name ? `🏷 Rename result title… (${existing.name})` : '🏷 Add result title…',
        onClick: () => openTitlePrompt(stmt, existing.name || ''),
      });
    }
    // "Copy columns to clipboard" — only appears when the click landed on
    // an identifier that resolves (via the cached completion schema) to a
    // table or view. Bare comma-separated column names — most pasteable
    // form for SELECT / INSERT column lists. No backticks (works for any
    // identifier that isn't a reserved word; users with edge-case names
    // can backtick them post-paste).
    const table = ident ? resolveTableAt(sqlText(), pos) : null;
    if (table) {
      // Open in the host's table-editor inner tab — same destination as the
      // schema browser's "Edit Table in Tab" action. Only wired when the host
      // gave us onOpenTableEdit (Browser does; standalone QueryPanel usage
      // wouldn't, but that doesn't happen in the current app shape).
      if (props.onOpenTableEdit) {
        items.push({
          label: `✎ Edit table ${table.database}.${table.name}`,
          onClick: () => props.onOpenTableEdit(table.database, table.name),
        });
      }
      if (table.columns.length > 0) {
        // Open the column-expander with this table preselected (same dialog as
        // Ctrl+Shift+E on a selected table) — pick/exclude columns, set an alias,
        // then Generate (insert) or copy the preview.
        items.push({
          label: `⛶ Expand columns of ${table.name}…`,
          onClick: () => setExpandReq({
            statementText: stmt ? stmt.text : '',
            fromTables: [{ db: table.database, table: table.name, alias: aliasForTable(stmt ? stmt.text : '', table.name, table.database) }],
            selectedText: table.name,
            insert: (text) => {
              const p = view.state.selection.main.head;
              view.dispatch({ changes: { from: p, insert: text }, selection: { anchor: p + text.length } });
              view.focus();
            },
          }),
        });
      }
    }
    if (ident) {
      items.push({
        label: `🔍 Find usages of "${ident.length > 28 ? ident.slice(0, 28) + '…' : ident}"`,
        onClick: () => openFindUsages(ident),
      });
    }
    if (items.length === 0) {
      items.push({ label: '🔍 Find usages…', onClick: () => openFindUsages('') });
    }

    // Format actions — statement-scoped when the click landed INSIDE a statement;
    // the whole-document variants only on whitespace (right-click a blank line /
    // gutter), so the menu shows one set, not both.
    items.push({ divider: true });
    if (stmt && !onWhitespace) {
      items.push({ label: '⌗ Format statement (full)',          onClick: () => formatRange(view, stmt.start, stmt.end, 'full') });
      items.push({ label: '↔ Single-line statement',            onClick: () => formatRange(view, stmt.start, stmt.end, 'oneline') });
      items.push({ label: '⏷ Change case in statement',         onClick: () => formatRange(view, stmt.start, stmt.end, 'case') });
      items.push({ label: '↳ Indent statement (routine body)',  onClick: () => formatRange(view, stmt.start, stmt.end, 'indent') });
    } else {
      items.push({ label: '⌗ Format all statements',       onClick: () => formatAll(view, 'full') });
      items.push({ label: '↔ Single-line all statements',  onClick: () => formatAll(view, 'oneline') });
      items.push({ label: '⏷ Change case in all',          onClick: () => formatAll(view, 'case') });
      items.push({ label: '↳ Indent routine bodies',       onClick: () => formatAll(view, 'indent') });
    }
    // Paste newline-delimited clipboard text as a comma list (plain or quoted).
    items.push({ divider: true }, ...commaPasteMenuItems(view));
    // Stamp each clipboard line into a custom SQL template (e.g. UPDATE per ID).
    items.push({
      label: '📋 Paste with template…',
      onClick: () => setPasteTemplateView(view),
    });
    setEditorCtxMenu({ x: e.clientX, y: e.clientY, items });
  };

  // Paste-with-template modal: holds the editor view to insert into on submit.
  // Cleared on close. The actual expand+insert happens via onPaste below.
  const [pasteTemplateView, setPasteTemplateView] = createSignal(null);
  const onPasteTemplateConfirm = (text) => {
    const v = pasteTemplateView();
    if (!v || !text) return;
    v.dispatch(v.state.replaceSelection(text));
    v.focus();
  };

  // Title-prompt: open a small modal with an existing-name-aware text input.
  // On submit we either INSERT a new `-- @name: <title>` line just before
  // the statement's first SQL token, or REPLACE the existing annotation
  // (any form) so a rename doesn't leave a stale one behind.
  const openTitlePrompt = (stmt, existingName) => {
    setTitlePrompt({
      stmtFrom: stmt.start,
      stmtTo: stmt.end,
      existingName,
      draft: existingName,
    });
    queueMicrotask(() => {
      titleInputRef?.focus();
      titleInputRef?.select?.();
    });
  };
  const submitTitlePrompt = () => {
    const p = titlePrompt();
    if (!p || !editorView) { setTitlePrompt(null); return; }
    const newName = (p.draft || '').trim();
    // Re-extract on submit: the statement text may have shifted if the user
    // edited elsewhere in the doc. (Cheap; the parser is fast.)
    const stmtText = editorView.state.sliceDoc(p.stmtFrom, p.stmtTo);
    const stripped = extractStatementName(stmtText); // returns the statement minus any existing annotation
    let replacement;
    if (newName === '') {
      // Empty submit on an existing-name statement = "remove the name".
      // Empty on a no-name statement = no-op (we'd be inserting blank).
      replacement = stripped.sql;
    } else {
      // Insert the annotation as the FIRST line of the statement (above any
      // existing description comments), so future runs pick it up via the
      // preamble scan and the user sees it immediately.
      replacement = `-- @name: ${newName}\n${stripped.sql}`;
    }
    editorView.dispatch({
      changes: { from: p.stmtFrom, to: p.stmtTo, insert: replacement },
    });
    setTitlePrompt(null);
    editorView.focus();
  };

  // Query profiler modal — opened from the Explain menu's "Profile" item.
  const [profileSql, setProfileSql] = createSignal(null);

  const runExplain = async (mode) => {
    if (running()) return;
    const cursor = editorView?.state?.selection?.main?.head ?? sqlText().length;
    const stmt = statementAtCursor(sqlText(), cursor);
    if (!stmt) { logInfo('No statement under cursor.'); return; }
    if (mode === 'profile') {
      // Profiler runs in its own modal (executes the statement) — strip a
      // leading ~name~ tag so the raw SQL is profiled.
      const named = extractStatementName(stmt.text);
      setProfileSql(named.name ? named.sql : stmt.text);
      return;
    }
    if (mode === 'plan+actual') { await runPlanAndActual(stmt); return; }
    // MariaDB's TREE-text ANALYZE is `ANALYZE <stmt>` (no EXPLAIN keyword);
    // MySQL's is `EXPLAIN ANALYZE <stmt>`. FORMAT=JSON + plain EXPLAIN are the
    // same across both vendors.
    const isMariaDB = serverInfo()?.flavour === 'mariadb';
    const prefix = mode === 'json' ? 'EXPLAIN FORMAT=JSON '
                 : mode === 'analyze' ? (isMariaDB ? 'ANALYZE ' : 'EXPLAIN ANALYZE ')
                 : 'EXPLAIN ';
    // Re-attach the user-supplied `~name~` (if any) in front of the EXPLAIN
    // keyword so runWithParams can pick it up — otherwise the prefix would
    // bury it inside the statement and the result tab would be unlabelled.
    const named = extractStatementName(stmt.text);
    const text = named.name
      ? `~${named.name}~${prefix}${named.sql}`
      : `${prefix}${stmt.text}`;
    await runWithParams([{ ...stmt, text }], { explain: mode });
  };

  // Plan + Actual orchestration: run EXPLAIN FORMAT=JSON to get the estimate,
  // then EXPLAIN ANALYZE FORMAT=JSON for the actuals. Both go to ONE result tab
  // so ExplainTree can render the merged overlay. Falls back to TREE-format
  // EXPLAIN ANALYZE when the JSON variant isn't supported (MySQL 8.0.18–8.0.31);
  // ExplainTree surfaces the TREE text below the tree rather than merging it.
  //
  // Resolves :params via the same prompt path the normal run uses, so a query
  // with named parameters can still be plan+actual'd. Bypasses runWithParams +
  // execOne because we need two backend round-trips to produce one tab — the
  // existing pipeline assumes one statement → one tab.
  const runPlanAndActual = async (stmt) => {
    if (!sessionId()) {
      try { await openSession(); }
      catch (e) { logError('No active session: ' + e.message); return; }
    }
    primeAudio();
    // Strip ~name~ first so it can title the merged result tab, and so
    // EXPLAIN doesn't see the prefix.
    const named = extractStatementName(stmt.text);
    const userLabel = named.name;
    const baseText = named.sql;
    // :param prompt + bind (same shape as runWithParams).
    const { params } = extractParams(baseText);
    if (params.length > 0) {
      const values = await promptParams(params, baseText);
      if (values === null) return;
      try { await withReconnect((sid) => setSessionVars(sid, values)); }
      catch (e) { logError('Failed to set parameters: ' + e.message); return; }
    }
    const effective = params.length > 0 ? extractParams(baseText).rewritten : baseText;

    setRunning(true);
    try {
      // Step 1 — EXPLAIN FORMAT=JSON. Mandatory: without it there's no tree
      // to overlay actuals onto.
      const explainStmt = 'EXPLAIN FORMAT=JSON ' + effective;
      const planMsg = pushStatement(explainStmt, userLabel);
      let explainJson = '';
      try {
        const r = await withReconnect((sid) => executeQuery(sid, explainStmt, false));
        if (r.error) {
          // Old MariaDB / MySQL 5.5 → no FORMAT=JSON. Plan+Actual is built on
          // the JSON form, so we can't merge actuals without it. Bail with a
          // friendly explanation pointing at the alternatives that DO work.
          if (isExplainSyntaxUnsupported(r.error)) {
            updateStatement(planMsg, {
              status: 'error',
              result: 'Server doesn\'t support EXPLAIN FORMAT=JSON — Plan+Actual needs MariaDB 10.1+ or MySQL 5.6+. Use "Explain (tabular)" for the plan and "Explain Analyze (text)" separately on MariaDB 10.1+ / MySQL 8.0.18+.',
              durationMs: r.durationMs,
            });
          } else {
            updateStatement(planMsg, { status: 'error', result: 'Error: ' + r.error, durationMs: r.durationMs });
          }
          return;
        }
        explainJson = String((r.resultSets || [])[0]?.rows?.[0]?.[0] ?? '');
        updateStatement(planMsg, { status: 'ok', result: 'Plan (FORMAT=JSON)', durationMs: r.durationMs });
      } catch (e) {
        updateStatement(planMsg, { status: 'error', result: e.message });
        return;
      }

      // Step 2 — ANALYZE FORMAT=JSON. Best-effort: if the server is older and
      // rejects FORMAT=JSON, fall back to plain TREE text so the user still
      // sees actuals (just not merged onto the tree). NOTE: MariaDB and MySQL
      // diverge here — MariaDB uses `ANALYZE FORMAT=JSON <stmt>` (no EXPLAIN
      // keyword), MySQL uses `EXPLAIN ANALYZE FORMAT=JSON <stmt>`.
      let analyzeJson = '', analyzeText = '';
      const isMariaDB = serverInfo()?.flavour === 'mariadb';
      const analyzeJsonStmt = (isMariaDB ? 'ANALYZE FORMAT=JSON ' : 'EXPLAIN ANALYZE FORMAT=JSON ') + effective;
      const analyzeMsg = pushStatement(analyzeJsonStmt, userLabel);
      try {
        const r = await withReconnect((sid) => executeQuery(sid, analyzeJsonStmt, false));
        if (r.error) {
          // Older server — log it and try the TREE-text variant.
          updateStatement(analyzeMsg, {
            status: 'error',
            result: 'ANALYZE FORMAT=JSON not supported on this server — retrying with TREE format. ' + r.error,
            durationMs: r.durationMs,
          });
          // MariaDB's TREE-text variant is just `ANALYZE <stmt>`; MySQL's is
          // `EXPLAIN ANALYZE <stmt>`. Both produce the same kind of indented
          // text output.
          const treeStmt = (isMariaDB ? 'ANALYZE ' : 'EXPLAIN ANALYZE ') + effective;
          const treeMsg = pushStatement(treeStmt, userLabel);
          try {
            const r2 = await withReconnect((sid) => executeQuery(sid, treeStmt, false));
            if (r2.error) {
              updateStatement(treeMsg, { status: 'error', result: 'Error: ' + r2.error, durationMs: r2.durationMs });
            } else {
              analyzeText = String((r2.resultSets || [])[0]?.rows?.[0]?.[0] ?? '');
              updateStatement(treeMsg, { status: 'ok', result: 'ANALYZE (TREE text)', durationMs: r2.durationMs });
            }
          } catch (e) {
            updateStatement(treeMsg, { status: 'error', result: e.message });
          }
        } else {
          analyzeJson = String((r.resultSets || [])[0]?.rows?.[0]?.[0] ?? '');
          updateStatement(analyzeMsg, { status: 'ok', result: 'ANALYZE (FORMAT=JSON)', durationMs: r.durationMs });
        }
      } catch (e) {
        updateStatement(analyzeMsg, { status: 'error', result: e.message });
      }

      // Publish the merged result.
      publishResult({
        label: userLabel || 'Plan + Actual',
        columns: [], rows: [],
        sql: stmt.text,
        explainJson,
        analyzeJson: analyzeJson || undefined,
        analyzeText: analyzeText || undefined,
      });
      if (querySoundNotify()) playSuccess();
    } finally {
      setRunning(false);
    }
  };


  // ── Parameterized queries (:name / :name#type#) ───────────────────────────
  // promptParams shows the modal and resolves with a { name: value } map (or
  // null if cancelled). Last-entered values are remembered per name for this
  // tab session so re-runs pre-fill. A NULL checkbox binds SQL NULL.
  let paramMemory = {};
  const [paramPrompt, setParamPrompt] = createSignal(null); // { params, values, nulls, sql }
  let paramResolve = null;
  const promptParams = (params, sql) => new Promise((resolve) => {
    paramResolve = resolve;
    const values = {}, nulls = {};
    for (const p of params) { values[p.name] = paramMemory[p.name] ?? ''; nulls[p.name] = false; }
    setParamPrompt({ params, values, nulls, sql: sql || '' });
  });
  const setParamValue = (name, v) => setParamPrompt((pp) => ({ ...pp, values: { ...pp.values, [name]: v } }));
  const setParamNull  = (name, v) => setParamPrompt((pp) => ({ ...pp, nulls:  { ...pp.nulls,  [name]: v } }));
  const submitParams = () => {
    const pp = paramPrompt();
    if (!pp) return;
    const out = {};
    for (const p of pp.params) {
      if (pp.nulls[p.name]) { out[p.name] = null; paramMemory[p.name] = ''; continue; }
      out[p.name] = coerceParam(pp.values[p.name], p.type);
      paramMemory[p.name] = pp.values[p.name];
    }
    setParamPrompt(null);
    paramResolve?.(out);
    paramResolve = null;
  };
  const cancelParams = () => { setParamPrompt(null); paramResolve?.(null); paramResolve = null; };

  // "Fill into a new tab & run": substitute the entered values as LITERALS into a
  // self-contained copy of the query, open it in a new query tab, and auto-run it
  // there — instead of binding @vars + running in place. Handy to keep/inspect/
  // share the exact query that ran. Cancels the in-place run (resolve null).
  const fillParamsIntoNewTab = () => {
    const pp = paramPrompt();
    if (!pp || !props.onOpenQueryWith) return;
    const literals = {};
    for (const p of pp.params) {
      literals[p.name] = pp.nulls[p.name] ? 'NULL' : sqlLiteral(coerceParam(pp.values[p.name], p.type));
      paramMemory[p.name] = pp.nulls[p.name] ? '' : pp.values[p.name];
    }
    const filled = substituteParams(pp.sql || '', literals);
    setParamPrompt(null);
    paramResolve?.(null); paramResolve = null; // don't also run in place
    props.onOpenQueryWith(currentDb(), filled, true); // new tab, auto-run
  };

  // ── No-WHERE write guard ──────────────────────────────────────────────────
  // confirmUnscoped shows the modal listing UPDATE/DELETE statements that have
  // no WHERE (would touch every row) and resolves true (run anyway) / false.
  const [unsafePrompt, setUnsafePrompt] = createSignal(null); // { items: [{kind, sql}] }
  let unsafeResolve = null;
  const confirmUnscoped = (items) => new Promise((resolve) => {
    unsafeResolve = resolve;
    setUnsafePrompt({ items });
  });
  const resolveUnsafe = (ok) => { setUnsafePrompt(null); unsafeResolve?.(ok); unsafeResolve = null; };

  // ── Implicit-commit guard ─────────────────────────────────────────────────
  // confirmImplicitCommit warns that DDL / BEGIN / LOCK / account statements
  // will implicitly COMMIT the open transaction (MySQL semantics, irreversible)
  // and resolves true (commit & run) / false (cancel).
  const [commitPrompt, setCommitPrompt] = createSignal(null); // { items: [{kind, sql}] }
  let commitResolve = null;
  const confirmImplicitCommit = (items) => new Promise((resolve) => {
    commitResolve = resolve;
    setCommitPrompt({ items });
  });
  const resolveCommit = (ok) => { setCommitPrompt(null); commitResolve?.(ok); commitResolve = null; };

  const stopQuery = async () => {
    const sid = sessionId();
    if (!sid) return;
    try {
      await cancelQuery(sid);
      logInfo('Cancellation requested (KILL QUERY sent).');
    } catch (e) {
      toast.error('Cancel failed: ' + e.message);
    }
  };

  // COMMIT/ROLLBACK are only offered while in a transaction, and the reaper never
  // evicts an in-tx session — so these don't auto-reconnect (a reconnect would
  // commit a brand-new empty transaction). A 404 here means the session was
  // killed externally (Sessions panel); the tx is already gone, so just reset.
  // When a result tab is in edit mode, COMMIT/ROLLBACK first flush the grid's
  // pending row, then EXIT edit mode (refresh the result so it reflects the
  // committed/rolled-back state). A 404 means the session was killed externally —
  // drop edit mode cleanly.
  //
  // Leave edit mode after the transaction has already ended (commit or rollback).
  // Mirrors doneResultEdit's tail minus the rollback it does for a mid-edit bail.
  const exitResultEditAfterTx = async (editTab) => {
    setEditResultTab(null);
    setEditPreview('');
    clearDraftsFn?.();
    clearDirtyFn?.();
    await refreshResultTab(editTab);
  };

  // Auto-commit mode has no Commit, so result-grid new-row drafts flush here —
  // each INSERT commits on its own (existing-cell edits already saved on row-leave).
  const saveResultDrafts = async () => {
    const editTab = editResultTab();
    if (flushResultGrid) { try { await flushResultGrid(); } catch (_) {} }
    if (flushDraftsFn) {
      const res = await flushDraftsFn();
      if (res?.validation?.length) {
        toast.error('A new row is missing a required value — fill it (or remove the row) first.');
        return;
      }
    }
    if (editTab) await refreshResultTab(editTab);
  };

  const commit = async () => {
    const sid = sessionId();
    if (!sid) return;
    const editTab = editResultTab();
    if (editTab && flushResultGrid) { try { await flushResultGrid(); } catch (_) {} }
    // Send pending new-row drafts as INSERTs before committing.
    if (editTab && flushDraftsFn) {
      const res = await flushDraftsFn();
      if (res?.validation?.length) {
        toast.error('A new row is missing a required value — fill it (or remove the row) before committing.');
        return;
      }
      if (res?.aborted) {
        setInTx(false); clearDraftsFn?.();
        await refreshResultTab(editTab); await reBeginForEdit();
        return;
      }
    }
    try {
      await commitTransaction(sid);
      setInTx(false);
      logOK('COMMIT');
      if (editTab) await exitResultEditAfterTx(editTab); // auto-exit edit mode after commit
    } catch (e) {
      if (await handleSessionLost(e, 'commit')) return;
      if (e?.status === 404) { setInTx(false); if (editTab) setEditResultTab(null); logError('COMMIT not done — the session was closed and its transaction rolled back.'); return; }
      logError('COMMIT failed: ' + e.message);
    }
  };

  const rollback = async () => {
    const sid = sessionId();
    if (!sid) return;
    const editTab = editResultTab();
    if (editTab && flushResultGrid) { try { await flushResultGrid(); } catch (_) {} }
    try {
      await rollbackTransaction(sid);
      setInTx(false);
      logInfo('ROLLBACK');
      if (editTab) await exitResultEditAfterTx(editTab); // auto-exit edit mode after rollback
    } catch (e) {
      if (await handleSessionLost(e, 'rollback')) return;
      if (e?.status === 404) { setInTx(false); if (editTab) setEditResultTab(null); logInfo('Transaction already ended — the session was closed.'); return; }
      logError('ROLLBACK failed: ' + e.message);
    }
  };

  // ── Editable result grid: edit-mode lifecycle ─────────────────────────────
  // startResultEdit enters edit mode on the active editable result tab: ensure a
  // session, BEGIN a transaction (so edits are atomic + viewable as SQL before
  // commit), and mark the tab editable. Mirrors the Browser data grid's
  // deliberate edit-mode entry.
  const startResultEdit = async () => {
    if (running()) { toast.error('Stop the running query before editing.'); return; }
    const tab = activeTabBody();
    if (!tab?.editable) return;
    if (!sessionId()) {
      try { await openSession(); }
      catch (e) { logError('No active session and reconnect failed: ' + e.message); return; }
    }
    try {
      // Auto-commit mode (editInTransaction off): skip BEGIN — each row write
      // commits on its own. The row-edit handler is gated on the session, not the
      // transaction, so edits still flow.
      if (editInTransaction() && !inTx()) { await withReconnect((sid) => beginTransaction(sid)); setInTx(true); logInfo(`BEGIN — editing ${tab.editable.table}`); }
      else if (!editInTransaction()) { logInfo(`Auto-commit edit mode — ${tab.editable.table} (each change is saved immediately)`); }
      setEditResultTab(tab.id);
    } catch (e) {
      logError('Could not start editing: ' + e.message);
    }
  };

  // Exit edit mode WITHOUT committing — rolls back any uncommitted edits (like
  // the Browser grid's "exit edit"), then refreshes the result to discard the
  // optimistic local mutations.
  const doneResultEdit = async () => {
    const editTab = editResultTab();
    if (!editTab) return;
    const sid = sessionId();
    if (flushResultGrid) { try { await flushResultGrid(); } catch (_) {} }
    if (sid && inTx()) {
      try { await rollbackTransaction(sid); logInfo('ROLLBACK (exit edit)'); }
      catch (e) { if (await handleSessionLost(e, 'rollback')) return; }
    }
    setInTx(false);
    setEditResultTab(null);
    setEditPreview('');
    clearDraftsFn?.();
    await refreshResultTab(editTab);
  };

  // After a Commit/Rollback inside edit mode, re-open a transaction so the next
  // edit is covered. Failure drops edit mode rather than leaving it half-armed.
  const reBeginForEdit = async () => {
    if (!editResultTab()) return; // edit mode already ended (e.g. the session was lost)
    const sid = sessionId();
    if (!sid) { setEditResultTab(null); return; }
    try { await beginTransaction(sid); setInTx(true); logInfo('BEGIN'); }
    catch (e) {
      if (await handleSessionLost(e, 'BEGIN')) return;
      logError('BEGIN failed: ' + e.message); setEditResultTab(null);
    }
  };

  // The edit session was killed externally (404) — abandon edit mode cleanly.
  const handleResultEditSessionLost = () => {
    toast.error(inTx()
      ? 'Edit session was closed — its open transaction was rolled back (uncommitted edits are gone). Re-enter edit mode to continue.'
      : 'Edit session was closed. Re-enter edit mode to continue.');
    setInTx(false);
    setEditResultTab(null);
    setEditPreview('');
    clearDraftsFn?.();
  };

  // Re-run a result tab's SQL and replace its rows/columns in place (keeps the
  // tab + its pinned/width state). Used after commit/rollback so the grid shows
  // the persisted truth, and re-detects editability.
  const refreshResultTab = async (tabId) => {
    const idx = resultTabs.findIndex((t) => t.id === tabId);
    if (idx < 0) return;
    const tab = resultTabs[idx];
    if (!tab.sql || !sessionId()) return;
    try {
      // refreshResultTab re-runs the same SELECT after a row edit — it's a read,
      // not a write, so the safe-updates flag is moot. Mode→bool keeps the
      // server happy on the strict path.
      const r = await withReconnect((sid) => executeQuery(sid, tab.sql, querySafeMode() === 'strict'));
      setInTx(!!r.inTransaction);
      const rs = (r.resultSets || [])[0];
      if (rs) {
        setResultTabs(idx, (t) => ({
          ...t,
          columns: rs.columns || [],
          rows: rs.rows || [],
          truncated: rs.truncated,
          editable: rs.editable || null,
        }));
      }
    } catch (e) {
      logError('Could not refresh result: ' + e.message);
    }
  };

  // PK-rename confirmation modal state (mirrors Browser.jsx). Held inside the
  // in-flight handleResultRowEdit until the user picks Confirm or Cancel; on
  // cancel we return `{ cancelled: true }` so the Grid drops the dirty markers
  // it had already added.
  const [pkConfirm, setPKConfirm] = createSignal(null);

  // Grid → us: a row left edit with buffered changes. One /update-row POST with
  // every changed column; the returned display SQL is echoed into the message
  // log and the row is optimistically mutated so the change shows immediately.
  const handleResultRowEdit = async (rowIdx, changes, meta) => {
    const sid = sessionId();
    const idx = resultTabs.findIndex((t) => t.id === editResultTab());
    if (!sid || idx < 0) return;
    const tab = resultTabs[idx];
    if (!tab.editable || !changes || Object.keys(changes).length === 0) return;
    // The row the edit was made on (the Grid hands it over: after a sort or
    // filter, rowIdx may point at another record).
    const row = meta?.row || gridRows()[rowIdx];
    if (!row) return;
    const ed = tab.editable;
    const pkVals = pkValuesFrom(tab, row);
    // PK rename gate — same flow as the data tab. Any PK column in the SET map
    // opens the FK-aware confirmation modal; cancel signals back to the Grid
    // via `{ cancelled }` so the dirty markers come off.
    const changedPKs = (ed.pkColumns || []).filter(
      (p) => Object.prototype.hasOwnProperty.call(changes, p)
    );
    if (changedPKs.length > 0) {
      const oldPK = {};
      const newPK = {};
      for (const p of changedPKs) {
        const ci = colIdx(tab, p);
        oldPK[p] = ci >= 0 ? row[ci] : null;
        newPK[p] = changes[p];
      }
      const ok = await new Promise((resolve) =>
        setPKConfirm({ database: ed.database, table: ed.table, oldPK, newPK, resolve }));
      setPKConfirm(null);
      if (!ok) return { cancelled: true };
    }
    const msgId = pushStatement(`UPDATE \`${ed.database}\`.\`${ed.table}\` …`);
    try {
      const r = await editCall((s) => updateRow(s, {
        database: ed.database, table: ed.table,
        pkColumns: ed.pkColumns, pkValues: pkWireValues(tab, pkVals),
        setColumns: writeValues(changes, (c) => colType(tab, c)),
        onError: editError(),
      }), 'row edit');
      if (r.error) {
        updateStatement(msgId, { status: 'error', sql: r.sql, result: `Error: ${r.error}`, warnings: r.warnings });
        if (r.autoRolledBack) {
          setInTx(false);
          logInfo('Transaction auto-ROLLBACK on error');
          await refreshResultTab(tab.id);
          await reBeginForEdit();
        }
        return { cancelled: true }; // nothing was saved — drop the staged-edit marker
      }
      updateStatement(msgId, {
        status: 'ok', sql: r.sql,
        result: `OK — ${r.rowsAffected} row${r.rowsAffected === 1 ? '' : 's'} affected`,
        warnings: r.warnings,
      });
      setInTx(!!r.inTransaction);
      if (r.warnings?.length) logInfo('⚠ The server raised warnings for that row — it may have stored something other than what was typed (the grid shows the stored values).');
      // Optimistic local mutate so the grid reflects the change without refetch.
      // Locate the RAW row by PK (the rowIdx was a displayed-view position).
      // If the server returned newPKValues (a PK column was rewritten), use the
      // canonical post-UPDATE PK for those columns — wins over the typed value
      // when the engine coerced/trimmed. With warnings the server also sends the
      // row as stored (`row`), which wins over everything typed.
      const rawIdx = rawIndexByPk(tab, pkVals);
      if (rawIdx >= 0) {
        const nextRows = tab.rows.slice();
        nextRows[rawIdx] = nextRows[rawIdx].slice();
        for (const [c, v] of Object.entries(changes)) {
          const ci = colIdx(tab, c);
          if (ci >= 0) nextRows[rawIdx][ci] = v;
        }
        if (Array.isArray(r.newPKValues) && r.newPKValues.length === ed.pkColumns.length) {
          for (let i = 0; i < ed.pkColumns.length; i++) {
            const ci = colIdx(tab, ed.pkColumns[i]);
            if (ci >= 0) nextRows[rawIdx][ci] = r.newPKValues[i];
          }
        }
        if (r.row && typeof r.row === 'object') {
          for (const [c, v] of Object.entries(r.row)) {
            const ci = colIdx(tab, c);
            if (ci >= 0 && tab.columns[ci].editable !== false) nextRows[rawIdx][ci] = v;
          }
        }
        setResultTabs(idx, 'rows', nextRows);
      }
    } catch (e) {
      updateStatement(msgId, { status: 'error', result: e.message });
      if (await handleSessionLost(e, 'row edit')) return { cancelled: true };
      if (e?.status === 404) handleResultEditSessionLost();
      return { cancelled: true };
    }
  };

  // Bulk-delete the gutter-selected rows in the editable result grid.
  // Each row's PK is read from the displayed (filtered/sorted) view; raw rows
  // are then located by PK and dropped from the tab's `rows` array on success.
  // In transaction mode Rollback undoes it — unless the table's engine is
  // non-transactional; in auto-commit mode it's permanent (the confirmation
  // says which).
  const handleResultDeleteSelected = async () => {
    const sid = sessionId();
    const idx = resultTabs.findIndex((t) => t.id === editResultTab());
    if (!sid || idx < 0) return;
    const tab = resultTabs[idx];
    if (!tab?.editable) return;
    const ed = tab.editable;
    if (!ed.pkColumns?.length) {
      toast.error('Bulk delete needs a primary key — this result has none.');
      return;
    }
    const selected = resultSelectedRowIdxs();
    if (!selected.length) return;
    if (!window.confirm(deleteConfirmMessage(selected.length, { inTransaction: editInTransaction(), engine: ed.engine }))) {
      return;
    }
    if (flushResultGrid) await flushResultGrid();
    const view = gridRows();
    // Build PK tuples from the displayed rows; remember each (rawIdx, pkVals)
    // pair so we can drop succeeded ones from tab.rows after the response.
    const targets = [];
    for (const i of selected) {
      const row = view[i];
      if (!row) continue;
      const pkVals = pkValuesFrom(tab, row);
      const rawIdx = rawIndexByPk(tab, pkVals);
      if (rawIdx < 0) continue;
      targets.push({ rawIdx, pkVals });
    }
    if (!targets.length) return;
    const msgId = pushStatement(`DELETE FROM \`${ed.database}\`.\`${ed.table}\` × ${targets.length}`);
    try {
      const r = await editCall((s) => deleteRows(s, {
        database: ed.database, table: ed.table,
        pkColumns: ed.pkColumns,
        rows: targets.map((t) => pkWireValues(tab, t.pkVals)),
        onError: editError(),
      }), 'delete');
      // Roll up outcomes into the single message. (For per-row visibility the
      // user can expand the SQL log — same model as bulk UPDATEs via flush.)
      const ok = r.succeededRows || 0;
      const total = r.totalRows || targets.length;
      if (r.autoRolledBack) {
        updateStatement(msgId, { status: 'error', sql: '', result: `Auto-rolled back: ${r.error || 'error'}` });
        setInTx(false);
        logInfo('Transaction auto-ROLLBACK on error');
        await refreshResultTab(tab.id);
        await reBeginForEdit();
        clearResultGridRowSelection?.();
        return;
      }
      if (r.error) {
        updateStatement(msgId, { status: 'error', result: `Stopped at ${ok}/${total}: ${r.error}` });
      } else if (ok < total) {
        updateStatement(msgId, { status: 'error', result: `Deleted ${ok}/${total} (some rows skipped — see log)` });
      } else {
        updateStatement(msgId, { status: 'ok', result: `OK — ${ok} row${ok === 1 ? '' : 's'} deleted` });
      }
      setInTx(!!r.inTransaction);
      // Drop the rows that actually succeeded. Outcomes are positionally
      // aligned with targets[]; a savepoint-rolled-back or errored outcome
      // means the raw row is still there.
      const outcomes = r.outcomes || [];
      const droppedRaw = new Set();
      for (let i = 0; i < targets.length && i < outcomes.length; i++) {
        const oc = outcomes[i];
        if (!oc.error && !oc.savepointRolledBack) droppedRaw.add(targets[i].rawIdx);
      }
      if (droppedRaw.size) {
        const nextRows = tab.rows.filter((_, i) => !droppedRaw.has(i));
        setResultTabs(idx, 'rows', nextRows);
      }
      clearResultGridRowSelection?.();
    } catch (e) {
      updateStatement(msgId, { status: 'error', result: e.message });
      if (await handleSessionLost(e, 'delete')) return;
      if (e?.status === 404) handleResultEditSessionLost();
    }
  };

  // Switch database — fires USE on the session and bumps recent-use. Reconnects
  // transparently if the session was reaped while idle.
  const switchDb = async (db) => {
    if (!db || db === currentDb()) return;
    try {
      await withReconnect((sid) => useDb(sid, db));
      setCurrentDb(db);
      logInfo(`USE \`${db}\``);
    } catch (e) {
      toast.error('USE failed: ' + e.message);
    }
  };

  // Switch this query tab to another connection in the same group: tear down the
  // current pinned session, repoint, keep the current database only if it exists
  // on the new connection (else reset the selector), then open a fresh session.
  // Blocked while a query is running or a transaction is open.
  const switchConnection = async (newId) => {
    if (!newId || newId === connId()) return;
    if (running()) { toast.error('A query is running — stop it before switching connection.'); return; }
    if (inTx())    { toast.error('Commit or roll back the transaction before switching connection.'); return; }

    const keepDb = currentDb();
    const oldSid = sessionId();
    batch(() => { setSessionId(null); setThreadId(null); setInTx(false); });
    if (oldSid) closeQuerySession(oldSid).catch(() => {});

    setConnId(newId); // re-keys the databases resource (dropdown reloads)

    // Keep the database only if the new connection has one by the same name.
    let nextDb = '';
    try {
      const r = await api.get(`/api/connections/${newId}/databases`);
      const names = (r.databases || []).map((d) => d.name);
      if (keepDb && names.includes(keepDb)) nextDb = keepDb;
    } catch { /* leave nextDb empty → user re-picks */ }
    setCurrentDb(nextDb);

    try {
      const r = await createQuerySession(newId, nextDb || null);
      batch(() => {
        setSessionId(r.sessionId);
        setThreadId(r.threadId);
        setCurrentDb(r.database || nextDb);
        setInTx(!!r.inTransaction);
      });
      props.onSessionIdChange?.(r.sessionId);
      props.onConnectionChange?.(newId);
      const cname = allConns()?.find((c) => c.id === newId)?.name ?? `#${newId}`;
      logInfo(`Switched to "${cname}"${r.database ? ` · ${r.database}` : ''} (thread ${r.threadId})`);
    } catch (e) {
      toast.error('Failed to open session on the new connection: ' + e.message);
      logError(e.message);
    }
  };

  const connSwitchTitle = () =>
    inTx()      ? 'Commit or roll back before switching connection'
    : running() ? 'Stop the running query before switching connection'
    : focusMode() ? 'Run this query tab against any connection (focus mode shows them all)'
    : 'Run this query tab against another connection in the same group';

  const clearMessages = () => setMessages([]);

  // Per-statement "⚠ N warnings" expand state, by message id (not persisted —
  // the warnings themselves ride the message log, but their open/closed UI state
  // is ephemeral).
  const [warnOpen, setWarnOpen] = createSignal(new Set());
  const toggleWarn = (id) => setWarnOpen((s) => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  // ── Saved queries actions ────────────────────────────────────────────────
  // Loading a saved query / history entry into a NON-empty editor asks whether
  // to append or replace, so unsaved work isn't silently clobbered. An empty
  // editor just loads.
  const [loadPrompt, setLoadPrompt] = createSignal(null); // { sql, label }
  const applyLoad = (sql, mode) => {
    if (mode === 'append') {
      const cur = sqlText();
      setSqlText(cur ? cur.replace(/\s*$/, '') + '\n\n' + sql : sql);
    } else {
      setSqlText(sql);
    }
  };
  const requestLoad = (sql, label) => {
    if (!sqlText().trim()) { applyLoad(sql, 'replace'); logInfo(`Loaded ${label}`); return; }
    setLoadPrompt({ sql, label });
  };
  const loadSaved = (q) => requestLoad(q.sqlText, `saved query "${q.title}"`);

  const [savingDlg, setSavingDlg] = createSignal(null); // { editingId?, title, description, sourceDatabase, sqlText, shared, shareTags }
  // Distinct user tags for the share picker — fetched only while the share toggle is on.
  const [tagOptions] = createResource(
    () => (savingDlg()?.shared ? 'load' : null),
    async () => { try { return (await api.get('/api/user-tags')).tags || []; } catch { return []; } },
  );
  const toggleShareTag = (t) => {
    const dlg = savingDlg();
    if (!dlg) return;
    const has = (dlg.shareTags || []).includes(t);
    setSavingDlg({ ...dlg, shareTags: has ? dlg.shareTags.filter((x) => x !== t) : [...(dlg.shareTags || []), t] });
  };
  const mine = (q) => q.ownerUserId === currentUser()?.id;

  const openSaveDialog = () => {
    if (!sqlText().trim()) { toast.error('Nothing to save — editor is empty.'); return; }
    // New save seeds the content from the current editor (still editable in the dialog).
    setSavingDlg({ title: '', description: '', sourceDatabase: currentDb(), sqlText: sqlText(), editingId: null, shared: false, shareTags: [] });
  };
  const closeSaveDialog = () => setSavingDlg(null);

  const commitSave = async () => {
    const dlg = savingDlg();
    if (!dlg || !dlg.title.trim()) { toast.error('Title required.'); return; }
    if (!dlg.sqlText.trim()) { toast.error('Query content is empty.'); return; }
    if (dlg.shared && (dlg.shareTags || []).length === 0) {
      toast.error('Pick at least one tag to share with (or turn sharing off).'); return;
    }
    try {
      const payload = {
        title: dlg.title.trim(),
        description: dlg.description.trim(),
        sourceDatabase: dlg.sourceDatabase || '',
        sqlText: dlg.sqlText, // the dialog's content — NOT the live editor
        shareTags: dlg.shared ? (dlg.shareTags || []) : [],
      };
      if (dlg.editingId) await updateSavedQuery(dlg.editingId, payload);
      else                await createSavedQuery(payload);
      refetchSaved();
      closeSaveDialog();
      toast.success(dlg.editingId ? 'Query updated' : 'Query saved');
    } catch (e) { toast.error(e.message); }
  };

  const renameSaved = (q) => {
    // Edit seeds the content from the saved query so it can be edited in place.
    setSavingDlg({
      title: q.title, description: q.description, sourceDatabase: q.sourceDatabase,
      sqlText: q.sqlText, editingId: q.id,
      shared: (q.shareTags || []).length > 0, shareTags: q.shareTags || [],
    });
  };

  const removeSaved = async (q) => {
    if (!confirm(`Delete saved query "${q.title}"?`)) return;
    try { await deleteSavedQuery(q.id); refetchSaved(); toast.success('Deleted'); }
    catch (e) { toast.error(e.message); }
  };

  const visibleSaved = () => {
    const list = savedList() || [];
    const f = savedFilter().trim().toLowerCase();
    if (!f) return list;
    return list.filter((q) => q.title.toLowerCase().includes(f)
                           || (q.description || '').toLowerCase().includes(f));
  };

  // History search — filter the recall list by SQL text (and DB name).
  const visibleHistory = () => {
    const list = historyList() ?? [];
    const f = historyFilter().trim().toLowerCase();
    if (!f) return list;
    return list.filter((h) => (h.sqlText || '').toLowerCase().includes(f)
                           || (h.database || '').toLowerCase().includes(f));
  };

  // ── Result-tab helpers ────────────────────────────────────────────────────
  // Result-tab lifecycle:
  //   - clearUnpinnedResults() runs ONCE at the start of every run, dropping
  //     the previous run's unpinned tabs. Pinned tabs survive.
  //   - publishResult() then APPENDS one tab per result set produced during the
  //     run, so a single run (e.g. 3 SELECTs, or a CALL with several result
  //     sets) keeps all of them side by side. Pinned tabs are never touched.
  const clearUnpinnedResults = () => {
    setResultTabs(resultTabs.filter((t) => t.pinned));
  };

  const publishResult = (rs) => {
    resultTabSeq += 1;
    const id = `res-${resultTabSeq}`;
    // filters/sortKeys are per-tab client-side view state (query results are a
    // static snapshot — see lib/gridview.js); explain tabs ignore them.
    // createdAt — captured at publish time so the "Copy as image" header can
    // show when the snapshot was taken; rs.createdAt wins so callers can pass
    // a deliberate timestamp (e.g. backend's response time) if they want to.
    // label fallback is applied AFTER the spread: callers pass `label: undefined`
    // when a statement carries no ~name~ / -- @name: annotation, and an explicit
    // undefined in the spread would otherwise clobber the default "Result #N".
    setResultTabs([...resultTabs, {
      id, filters: [], sortKeys: [], createdAt: Date.now(), pinned: false,
      ...rs,
      label: rs.label || `Result #${resultTabSeq}`,
    }]);
    maybeAutosaveResult(rs);
  };

  // Autosave a data result set to Result History (when the pref is on). Only real
  // result sets with columns are saved — not statement echoes (empty columns),
  // EXPLAIN plans, or results past the client row cap. Fire-and-forget.
  const maybeAutosaveResult = (rs) => {
    if (!queryAutosaveResults()) return;
    if (!Array.isArray(rs.columns) || rs.columns.length === 0 || !Array.isArray(rs.rows)) return;
    if (rs.explainJson || rs.explainText != null) return;
    if (rs.rows.length > AUTOSAVE_ROW_CAP) return;
    saveResult({
      connectionId: connId() || 0,
      database: currentDb() || '',
      sql: rs.sql || '',
      columns: rs.columns.map((c) => c.name),
      rows: rs.rows,
    }).catch(() => {});
  };

  // Focus the first result tab produced by the run just finished. `pinnedCount`
  // is the number of (pinned) tabs that survived clearUnpinnedResults(), so the
  // run's first new tab sits at that index. Falls back to Messages when the run
  // produced no result sets (e.g. only INSERT/UPDATE statements).
  const focusFirstNewResult = (pinnedCount) => {
    const first = resultTabs[pinnedCount];
    setActiveResultTab(first ? first.id : 'messages');
  };

  const togglePinTab = (id) => {
    const idx = resultTabs.findIndex((t) => t.id === id);
    if (idx < 0) return;
    setResultTabs(idx, 'pinned', !resultTabs[idx].pinned);
  };

  const closeResultTab = (id) => {
    const idx = resultTabs.findIndex((t) => t.id === id);
    if (idx < 0) return;
    const next = resultTabs.filter((t) => t.id !== id);
    setResultTabs(next);
    if (activeResultTab() === id) {
      // Prefer the left neighbour, then right, then messages.
      const fallback = next[idx - 1]?.id ?? next[idx]?.id ?? 'messages';
      setActiveResultTab(fallback);
    }
  };

  // Active tab body — used by the render.
  const activeTabBody = () => resultTabs.find((t) => t.id === activeResultTab()) || null;

  // A result tab carrying an EXPLAIN plan (JSON tree or ANALYZE text) rather than
  // a data grid — has no row grid, so the row-count badge, export, etc. are hidden.
  const isExplainTab = (t) => !!(t && (t.explainJson != null || t.explainText != null));

  // ── Client-side filter / sort for query results ───────────────────────────
  // Query results are a static snapshot, so filter+sort happen IN JS over the
  // loaded rows (lib/gridview.js) — unlike the Browser data grid, which
  // re-queries the server. State lives per result tab (filters/sortKeys), so
  // each tab keeps its own view. The Grid is fed the DERIVED rows (gridRows),
  // which also makes the selection footer reflect the filtered view. Editing
  // operates on this same view (PK/identity-based — see handleResultRowEdit), so
  // filter/sort can stay active while editing (TablePlus/DBeaver behaviour).
  const noValueOps = new Set(FILTER_OPS.filter((o) => o.noValue).map((o) => o.value));
  const isNoValueOp = (op) => noValueOps.has(op);
  const [filterPopover, setFilterPopover] = createSignal(null); // { column, rect, prefill }

  // viewRowsFor applies a tab's filters+sort; gridRows() is the active tab's view.
  const viewRowsFor = (tab) => (tab ? applyFilterSort(tab.rows || [], tab.columns || [], tab.filters, tab.sortKeys) : []);
  const gridRows = createMemo(() => viewRowsFor(activeTabBody()));
  const activeFilters  = () => activeTabBody()?.filters  || [];
  const activeSortKeys = () => activeTabBody()?.sortKeys || [];
  const isResultFiltered = () => activeFilters().length > 0;

  // Mutate the ACTIVE tab's view state in the resultTabs store.
  const setActiveTabField = (key, val) => {
    const i = resultTabs.findIndex((t) => t.id === activeResultTab());
    if (i >= 0) setResultTabs(i, key, val);
  };

  // Header left-click → open the filter popover (prefilled from the clipboard
  // like the Browser grid). Header sort-icon click → onResultSortToggle.
  const onResultHeaderClick = async (col, e) => {
    const rect = e.currentTarget.getBoundingClientRect(); // capture before the await
    const prefill = await readClipboardPrefill();
    // A clipboard-prefilled value defaults to "contains"; an empty open leaves op
    // undefined so the popover's own "contains" default applies.
    setFilterPopover({ column: col.name, rect, prefill, op: prefill ? 'like' : undefined, editIndex: null });
  };
  // Click a filter chip to edit it (reopen the popover prefilled with its op+value).
  const editResultFilter = (idx, e) => {
    const f = activeFilters()[idx];
    if (!f) return;
    setFilterPopover({ column: f.column, rect: e.currentTarget.getBoundingClientRect(), prefill: f.value ?? '', op: f.op, editIndex: idx });
  };
  // Sort cycle (shared with the Browser data grid via cycleSort), client-side per tab.
  const onResultSortToggle = (colName, ctrlKey) => setActiveTabField('sortKeys', cycleSort(activeSortKeys(), colName, ctrlKey));
  const applyResultFilter = (f) => {
    const idx = filterPopover()?.editIndex;
    if (idx == null) {
      setActiveTabField('filters', [...activeFilters(), { ...f, connector: 'AND' }]);
    } else {
      setActiveTabField('filters', activeFilters().map((old, k) => (k === idx ? { ...f, connector: old.connector } : old)));
    }
    setFilterPopover(null);
  };
  const removeResultFilter = (i) => setActiveTabField('filters', activeFilters().filter((_, k) => k !== i));
  const setResultConnector = (i, value) => setActiveTabField('filters', activeFilters().map((f, k) => (k === i ? { ...f, connector: value } : f)));
  const clearResultFilters = () => setActiveTabField('filters', []);

  // ── Editor/result splitter ────────────────────────────────────────────────
  // Drag the bar between editor and result panel to resize. Ratio is the
  // fraction of vertical space given to the editor; clamped + persisted.
  let splitContainerEl;
  const startSplitterDrag = (e) => {
    e.preventDefault();
    const host = splitContainerEl;
    if (!host) return;
    const move = (ev) => {
      const rect = host.getBoundingClientRect();
      if (rect.height <= 0) return;
      const ratio = (ev.clientY - rect.top) / rect.height;
      setQueryEditorSplit(ratio);
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  // ── Saved-queries sidebar splitter ──────────────────────────────────────────
  // Drag the bar on the sidebar's right edge to resize. Width is clamped +
  // persisted by the pref setter (mirrors the database-tree splitter).
  const onSidebarDrag = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = querySidebarWidth();
    const move = (ev) => setQuerySidebarWidth(startW + (ev.clientX - startX));
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div class="query-panel" ref={rootEl}>
      {/* ── Saved-queries sidebar ─────────────────────────────────────── */}
      <Show when={querySidebarVisible() && !compact()}>
      <aside class="query-sidebar" style={`width: ${querySidebarWidth()}px;`}>
        <div class="query-sidebar-header">
          <div class="query-sidebar-tabs">
            <button class="query-sidebar-tab" classList={{ active: sidebarTab() === 'saved' }}
                    onClick={() => setSidebarTab('saved')}>Saved</button>
            <button class="query-sidebar-tab" classList={{ active: sidebarTab() === 'history' }}
                    onClick={() => { setSidebarTab('history'); refetchHistory(); }}>History</button>
            <button class="query-sidebar-tab" classList={{ active: sidebarTab() === 'scrap' }}
                    onClick={() => { setSidebarTab('scrap'); refetchScraps(); }}
                    title="Recover the content of recently closed query tabs (kept 7 days)">Scrap</button>
            <button class="query-sidebar-tab" classList={{ active: sidebarTab() === 'outline' }}
                    onClick={() => setSidebarTab('outline')}
                    title="Jump between the statements in this editor">Outline</button>
          </div>
          {/* +Save lives on its own row, right-aligned, so a narrow sidebar
              doesn't clip it onto "Sa…". Only rendered for the Saved tab. */}
          <Show when={sidebarTab() === 'saved'}>
            <div class="query-sidebar-save-row">
              <button class="btn btn-sm btn-ghost" onClick={openSaveDialog} title="Save current editor as a named query">
                + Save
              </button>
            </div>
          </Show>
        </div>

        {/* Saved queries */}
        <Show when={sidebarTab() === 'saved'}>
          <input
            class="input objects-filter-input"
            style="margin:6px 8px;width:auto;"
            placeholder="Filter…"
            value={savedFilter()}
            onInput={(e) => setSavedFilter(e.target.value)}
          />
          <div class="query-saved-list">
            <Show when={!savedList.loading} fallback={<div class="muted" style="padding:8px 10px;">Loading…</div>}>
              <Show when={visibleSaved().length === 0}>
                <div class="muted" style="padding:8px 10px;font-size:12px;">No saved queries yet.</div>
              </Show>
              <For each={visibleSaved()}>
                {(q) => (
                  <div class="query-saved-item" onDblClick={() => loadSaved(q)}>
                    <div class="query-saved-title" onClick={() => loadSaved(q)} title="Click to load">
                      {q.title}
                      <Show when={(q.shareTags || []).length}>
                        <span class="sq-shared-badge" title={`Shared with: ${q.shareTags.join(', ')}`}>🔗</span>
                      </Show>
                    </div>
                    <Show when={q.description}>
                      <div class="query-saved-desc">{q.description}</div>
                    </Show>
                    <Show when={!mine(q)}>
                      <div class="query-saved-author muted">shared by {q.authorName || 'another user'}</div>
                    </Show>
                    <Show when={q.sourceDatabase}>
                      <div class="query-saved-db mono">{q.sourceDatabase}</div>
                    </Show>
                    {/* Only the author can edit/delete; shared queries are read-only. */}
                    <Show when={mine(q)}>
                      <div class="query-saved-actions">
                        <button class="btn-link" onClick={() => renameSaved(q)}>edit</button>
                        <button class="btn-link btn-link-danger" onClick={() => removeSaved(q)}>delete</button>
                      </div>
                    </Show>
                  </div>
                )}
              </For>
            </Show>
          </div>
        </Show>

        {/* Recall history — your query-tab statements; click to load */}
        <Show when={sidebarTab() === 'history'}>
          <input
            class="input objects-filter-input"
            style="margin:6px 8px;width:auto;"
            placeholder="Search history…"
            value={historyFilter()}
            onInput={(e) => setHistoryFilter(e.target.value)}
          />
          <div class="query-saved-list">
            <Show when={!historyList.loading} fallback={<div class="muted" style="padding:8px 10px;">Loading…</div>}>
              <Show when={(historyList() ?? []).length === 0}>
                <div class="muted" style="padding:8px 10px;font-size:12px;">No history yet — run a query.</div>
              </Show>
              <Show when={(historyList() ?? []).length > 0 && visibleHistory().length === 0}>
                <div class="muted" style="padding:8px 10px;font-size:12px;">No history matches “{historyFilter()}”.</div>
              </Show>
              <For each={visibleHistory()}>
                {(h) => (
                  <div class="query-history-item" onClick={() => loadHistory(h)} title={h.sqlText}>
                    <div class="query-history-sql mono">{firstLine(h.sqlText)}</div>
                    <div class="query-history-meta">
                      <span class={h.success ? 'hist-ok' : 'hist-err'}>{h.success ? '✓' : '✕'}</span>
                      <Show when={h.database}><span class="mono muted">{h.database}</span></Show>
                      <span class="muted">{relTime(h.createdAt)}</span>
                    </div>
                  </div>
                )}
              </For>
            </Show>
          </div>
        </Show>

        {/* Scrap — content of recently closed query tabs; click to reopen in a new tab */}
        <Show when={sidebarTab() === 'scrap'}>
          <div class="query-saved-list">
            <Show when={!scrapList.loading} fallback={<div class="muted" style="padding:8px 10px;">Loading…</div>}>
              <Show when={(scrapList() ?? []).length === 0}>
                <div class="muted" style="padding:8px 10px;font-size:12px;">
                  No scraps. Closed query tabs land here for 7 days — click one to reopen it.
                </div>
              </Show>
              <For each={scrapList() ?? []}>
                {(sc) => (
                  <div class="query-history-item" onClick={() => loadScrap(sc)}
                       title={`Reopen in a new tab\n\n${sc.sqlText}`}>
                    <div class="query-history-sql mono">{sc.title || firstLine(sc.sqlText)}</div>
                    <div class="query-history-meta">
                      <Show when={sc.database}><span class="mono muted">{sc.database}</span></Show>
                      <span class="muted">{relTime(sc.createdAt)}</span>
                      <button class="btn-link btn-link-danger" style="margin-left:auto;"
                              onClick={(e) => { e.stopPropagation(); removeScrap(sc); }}>delete</button>
                    </div>
                  </div>
                )}
              </For>
            </Show>
          </div>
        </Show>

        {/* Outline — statements in the current editor; click to jump the caret */}
        <Show when={sidebarTab() === 'outline'}>
          <div class="query-saved-list">
            <Show when={outline().length === 0}>
              <div class="muted" style="padding:8px 10px;font-size:12px;">No statements yet.</div>
            </Show>
            <For each={outline()}>
              {(item, i) => (
                <div class="query-outline-item" onClick={() => jumpToOutline(item)} title="Jump to this statement">
                  <span class="query-outline-num mono">{i() + 1}</span>
                  <span class="query-outline-label mono">{item.label}</span>
                </div>
              )}
            </For>
          </div>
        </Show>
      </aside>
      <div class="query-splitter" onPointerDown={onSidebarDrag}
           title="Drag to resize sidebar" />
      </Show>

      {/* ── Main column: toolbar + editor + result ─────────────────────── */}
      <div class="query-main">
        {/* Toolbar — hidden in results-only (compact) mode */}
        <Show when={!compact()}>
        <div class="query-toolbar">
          <button class="btn btn-sm btn-ghost"
                  onClick={() => setQuerySidebarVisible(!querySidebarVisible())}
                  title={querySidebarVisible() ? 'Hide the Saved / History panel' : 'Show the Saved / History panel'}>
            {querySidebarVisible() ? '«' : '»'}
          </button>
          <Show when={pickerConns().length > 1}>
            <label class="query-tool-label">Conn:</label>
            <select
              class="input query-conn-select"
              value={connId()}
              onChange={(e) => switchConnection(Number(e.target.value))}
              disabled={running() || inTx()}
              title={connSwitchTitle()}
            >
              <For each={pickerConns()}>
                {(c) => <option value={c.id} selected={c.id === connId()}>{c.name}</option>}
              </For>
            </select>
          </Show>
          <label class="query-tool-label">DB:</label>
          <select
            class="input query-db-select"
            value={currentDb()}
            onChange={(e) => switchDb(e.target.value)}
            disabled={!sessionId() || running() || editing()}
          >
            <Show when={!currentDb()}>
              <option value="">— select —</option>
            </Show>
            <For each={visibleDatabases()}>
              {(d) => <option value={d.name} selected={d.name === currentDb()}>{d.name}</option>}
            </For>
          </select>

          <RunMenu
            onRun={runAll}
            onRunCurrent={runCurrentOrSelection}
            onRunScript={runScript}
            disabled={running() || !sessionId() || editing()}
            disabledReason={editing() ? 'Finish editing the result (Commit/Rollback/Done) before running' : undefined}
          />
          <ExplainMenu onPick={runExplain} disabled={running() || !sessionId() || editing()}
                       serverInfo={serverInfo()} />
          <button class="btn btn-sm btn-ghost" onClick={openFindUsages}
                  title="Find usages — search this identifier across saved queries, scraps, history, forms, routines, triggers, and views.">
            🔍 Find usages
          </button>
          <button class="btn btn-sm btn-ghost" onClick={() => setHistoryOpen(true)}
                  title="Editor history — diff and revert to an earlier version of this tab's SQL (captured automatically as you edit).">
            🕘 History
          </button>
          <button class="btn btn-sm btn-ghost" onClick={() => setDslHelpOpen(true)}
                  title="Query DSL & editor extras — the expand batch-generation statement, :parameters, editor snippets.">
            📖 DSL
          </button>
          <FormatMenu onPick={(mode) => { if (editorView) formatEditor(editorView, mode); }}
                      autoFormat={editorAutoFormat()} onToggleAutoFormat={() => setEditorAutoFormat(!editorAutoFormat())}
                      lineWrap={editorLineWrap()} onToggleLineWrap={() => setEditorLineWrap(!editorLineWrap())} />
          <Show when={running()}>
            <button class="btn btn-sm btn-danger" onClick={stopQuery} title="Cancel running query (KILL QUERY)">
              ⏹ Stop
            </button>
          </Show>

          <span class="query-toolbar-sep" />

          <OptionsMenu
            safeMode={querySafeMode()} onSafeMode={setQuerySafeMode}
            transaction={txToggle()} onToggleTransaction={() => setTxToggle(!txToggle())}
            sound={querySoundNotify()}
            onToggleSound={() => {
              primeAudio();
              const next = !querySoundNotify();
              setQuerySoundNotify(next);
              if (next) playSuccess(); // confirm audio works + that it's now on
            }}
            autosaveResults={queryAutosaveResults()}
            onToggleAutosaveResults={() => setQueryAutosaveResults(!queryAutosaveResults())}
            notifyAvailable={notifyAvailable()}
            notify={notifyOnComplete()}
            onToggleNotify={() => setNotifyOnComplete(!notifyOnComplete())}
            onSyncSchema={syncSchema} schemaBusy={schemaSyncing()}
          />
          {/* In-transaction state stays in the toolbar (contextual, only while a tx is open). */}
          <Show when={inTx()}>
            <span class="query-tx-badge" title="Uncommitted changes — they hold locks until you Commit or Roll back">● Uncommitted</span>
            <button class="btn btn-sm" onClick={commit}>✓ Commit</button>
            <button class="btn btn-sm btn-danger" onClick={rollback}>↶ Rollback</button>
          </Show>

          <button class="btn btn-sm btn-ghost" onClick={() => setFocusMode(!focusMode())}
                  title={focusMode()
                    ? 'Exit distraction-free mode (Esc)'
                    : 'Distraction-free mode — hide everything but the editor tabs (Esc to exit)'}>
            ⛶ {focusMode() ? 'Exit focus' : 'Focus'}
          </button>

          <div class="spacer" />
          {/* Server version moved to the sidebar header (under the connection
              name); the thread id stays here — it's per query session. */}
          <span class="muted mono" style="font-size:11px;">
            <Show when={threadId() && queryShowThreadId()}>thread {threadId()}</Show>
          </span>
        </div>
        </Show>

        {/* Editor + splitter + result share a flex container so they can
            be sized by ratio. `flex-basis` carries the ratio; min-heights
            in CSS keep either pane from disappearing. In compact mode the
            editor + splitter are hidden and the result fills the panel. */}
        <div class="query-split" ref={splitContainerEl}>
        <Show when={!compact()}>
        {/* Editor */}
        <div class={`query-editor-wrap ${inTx() ? 'in-tx' : ''}`}
             style={`flex-basis: ${(queryEditorSplit() * 100).toFixed(2)}%;`}>
          <CodeMirrorSQL
            value={sqlText()}
            onInput={(v) => setSqlText(v)}
            onViewReady={(view) => {
              editorView = view;
              // Publish this editor's context to the Object Viewer (Ctrl+F12):
              // connection, database, and the identifier under the cursor. The
              // focused editor wins, so register on focus (and once now).
              const getCtx = () => ({ connectionId: connId(), database: currentDb(), word: wordAtCursor(view) });
              registerActiveEditor(getCtx);
              view.contentDOM.addEventListener('focusin', () => registerActiveEditor(getCtx));
            }}
            onContextMenu={onEditorContextMenu}
            autofocus={props.active}
            autoFormat={editorAutoFormat()}
            lineWrap={editorLineWrap()}
            schema={sqlSchema}
            onExpandColumns={setExpandReq}
            height="100%"
          />
        </div>

        <div class="query-splitter" onPointerDown={startSplitterDrag}
             title="Drag to resize" />
        </Show>

        {/* Result panel */}
        <div class="query-result"
             style={`flex-basis: ${compact() ? 100 : ((1 - queryEditorSplit()) * 100).toFixed(2)}%;`}>
          <div class="query-result-tabs">
            <button class="inner-tab"
                    classList={{ active: activeResultTab() === 'messages' }}
                    onClick={() => switchResultTab('messages')}>
              Messages
              <Show when={messages.length > 0}>
                <span class="inner-tab-badge"> {messages.length}</span>
              </Show>
            </button>
            <For each={resultTabs}>
              {(t) => (
                <button
                  class="inner-tab inner-tab-result"
                  classList={{ active: activeResultTab() === t.id, 'inner-tab-editing': editResultTab() === t.id }}
                  onClick={() => switchResultTab(t.id)}
                  title={`${t.label}${isExplainTab(t) ? '' : ` — ${t.rows.length} row${t.rows.length === 1 ? '' : 's'}`} in ${t.durationMs}ms${t.pinned ? ' (pinned — won’t be replaced)' : ''}${t.editable ? ' · editable' : ''}${t.sql ? `\n\n${t.sql}` : ''}`}
                >
                  <Show when={editResultTab() === t.id}>
                    <span class="inner-tab-edit-dot" title="Editing — uncommitted">✎</span>
                  </Show>
                  <span class="inner-tab-label">{t.label}</span>
                  <Show when={!isExplainTab(t)}>
                    <span class="inner-tab-badge"> {formatNumberWithSep(t.rows.length)}</span>
                  </Show>
                  <span
                    class={`inner-tab-pinbtn ${t.pinned ? 'pinned' : ''}`}
                    title={t.pinned ? 'Unpin — next query may overwrite this tab' : 'Pin — keep this result when running more queries'}
                    onClick={(e) => { e.stopPropagation(); togglePinTab(t.id); }}
                  >{t.pinned ? '📌' : '📍'}</span>
                  <span
                    class="inner-tab-close"
                    onClick={(e) => { e.stopPropagation(); closeResultTab(t.id); }}
                  >×</span>
                </button>
              )}
            </For>
            <div class="spacer" />
            <Show when={activeResultTab() !== 'messages' && activeTabBody()?.editable && !editing()}>
              <button class="btn btn-sm btn-ghost" onClick={startResultEdit} disabled={running()}
                      title={`Edit rows of ${activeTabBody()?.editable?.table} inline (single-table result with its primary key). ${editInTransaction() ? 'Wrapped in a transaction you Commit or Roll back.' : 'Auto-commit: each change saves immediately.'}`}>
                ✎ Edit
              </button>
            </Show>
            <Show when={activeResultTab() !== 'messages' && !isExplainTab(activeTabBody()) && activeTabBody()?.rows?.length}>
              <button class="btn btn-sm btn-ghost" classList={{ active: isCharting(activeResultTab()) }}
                      onClick={() => toggleChart(activeResultTab())}
                      title="Visualize this result set as a chart (bar / line / area / pie). Toggle back to the data grid.">
                📊 {isCharting(activeResultTab()) ? 'Grid' : 'Chart'}
              </button>
            </Show>
            <Show when={activeResultTab() !== 'messages' && !isCharting(activeResultTab()) && activeTabBody()?.rows?.length}>
              <button class="btn btn-sm btn-ghost" onClick={handleAutoFit}
                      title="Resize every column to fit its content (90th-percentile width). Mostly-empty columns shrink to the NULL tag. Drag any column afterwards to override.">
                ↔ Auto-fit
              </button>
            </Show>
            <Show when={activeResultTab() !== 'messages' && activeTabBody()?.sql && !isExplainTab(activeTabBody())}>
              <ExportMenu
                spec={() => ({ kind: 'query', connectionId: connId(), database: currentDb(), sql: activeTabBody()?.sql || '' })}
                columns={() => activeTabBody()?.columns || []}
                rows={() => gridRows()}
                displayOrder={() => (activeTabBody()?.columns || []).map((c) => c.name)}
                imageMeta={() => ({
                  label: activeTabBody()?.label,
                  sql: activeTabBody()?.sql,
                  database: currentDb(),
                  createdAt: activeTabBody()?.createdAt,
                  server: serverInfo()?.display,
                })}
                disabled={!activeTabBody()?.sql}
              />
            </Show>
            <Show when={activeResultTab() === 'messages' && messages.length > 0}>
              <button class="btn btn-sm btn-ghost" onClick={clearMessages}>Clear</button>
            </Show>
          </div>

          {/* Edit mode for an editable single-table result (transaction-wrapped,
              each row UPDATE shown in the message log): Commit / Rollback / Done
              and the row tools sit on their own row under the result tabs, so the
              tab strip keeps its width — mirrors the Browser's td-edit-toolbar. */}
          <Show when={activeResultTab() !== 'messages' && editResultTab() === activeResultTab()}>
            <div class="query-edit-toolbar td-edit-toolbar">
              <Show when={editInTransaction()} fallback={
                <span class="query-edit-pill query-edit-pill-auto" title={`Editing ${activeTabBody()?.editable?.table || ''} — auto-commit: each change saves immediately (no Rollback)`}>
                  ⚡ Editing {activeTabBody()?.editable?.table} · auto-commit
                </span>
              }>
                <span class="query-edit-pill" title={`Editing ${activeTabBody()?.editable?.table || ''} — changes are uncommitted`}>
                  ✎ Editing {activeTabBody()?.editable?.table}
                </span>
                <Show when={isTransactionalEngine(activeTabBody()?.editable?.engine) === false}>
                  <span class="query-edit-pill query-edit-pill-auto"
                        title={`${activeTabBody()?.editable?.engine} doesn't support transactions: every change is saved immediately and Rollback can't undo it.`}>
                    ⚠ {activeTabBody()?.editable?.engine}: no rollback
                  </span>
                </Show>
              </Show>
              <Show when={resultDirtyCount() > 0}>
                <span class="grid-changes-badge" title="Edited cells not yet committed">{resultDirtyCount()} change{resultDirtyCount() === 1 ? '' : 's'}</span>
              </Show>
              <Show when={editInTransaction()}>
                <button class="btn btn-sm" onClick={commit} title="Commit the edits and exit edit mode">✓ Commit</button>
                <button class="btn btn-sm btn-danger" onClick={rollback} title="Roll back the uncommitted edits and exit edit mode">↶ Rollback</button>
              </Show>
              <button class="btn btn-sm btn-ghost" onClick={doneResultEdit} title={editInTransaction() ? 'Exit edit mode — rolls back anything uncommitted' : 'Exit edit mode'}>✕ Done</button>
              <Show when={insertableTab()}>
                <span class="query-toolbar-sep" />
                <button class="btn btn-sm btn-ghost" onClick={() => rowInserter && rowInserter()}
                        title="Append a new (empty) row. You can also press Insert to add a row above the current one, or Arrow-Down on the last row.">
                  ＋ Append row
                </button>
              </Show>
              <Show when={!editInTransaction() && pendingInserts().length > 0}>
                <button class="btn btn-sm btn-primary" onClick={saveResultDrafts}
                        title="Insert the pending new row(s). Auto-commit is on, so they're saved immediately.">
                  💾 Save {pendingInserts().length} new row{pendingInserts().length === 1 ? '' : 's'}
                </button>
              </Show>
              <Show when={resultSelectedRowIdxs().length > 0}>
                <button class="btn btn-sm btn-danger" onClick={handleResultDeleteSelected}
                        title={`DELETE the selected rows by PK. ${deleteNote({ inTransaction: editInTransaction(), engine: activeTabBody()?.editable?.engine })}`}>
                  🗑 Delete {resultSelectedRowIdxs().length} row{resultSelectedRowIdxs().length === 1 ? '' : 's'}
                </button>
              </Show>
              <Show when={editInTransaction()}>
                <label class="td-edit-onerror" title="What to do if a row write fails when committing: Stop rolls the whole transaction back; Skip rolls back only the failing row (SAVEPOINT) and keeps the rest.">
                  On error:
                  <select class="input" value={editError()} onChange={(e) => setEditError(e.target.value)}>
                    <option value="stop">Stop &amp; roll back</option>
                    <option value="skip">Skip &amp; continue</option>
                  </select>
                </label>
              </Show>
            </div>
          </Show>

          {/* Filter chip strip — per-pair AND/OR connectors, mirrors the Browser
              grid's strip but applied client-side over the loaded result rows. */}
          <Show when={activeResultTab() !== 'messages' && !isExplainTab(activeTabBody()) && isResultFiltered()}>
            <div class="filter-strip">
              <span class="filter-strip-label">Filters:</span>
              <For each={activeFilters()}>
                {(f, i) => (
                  <>
                    <Show when={i() > 0}>
                      <select
                        class="input filter-conn"
                        value={f.connector || 'AND'}
                        onChange={(e) => setResultConnector(i(), e.target.value)}
                        title="Logical operator with the previous filter"
                      >
                        <option value="AND" selected={(f.connector || 'AND') === 'AND'}>AND</option>
                        <option value="OR"  selected={f.connector === 'OR'}>OR</option>
                      </select>
                    </Show>
                    <div class="filter-chip" style="cursor:pointer;" title="Click to edit this filter"
                         onClick={(e) => editResultFilter(i(), e)}>
                      <span class="filter-chip-col mono">{f.column}</span>
                      <span class="filter-chip-op">{formatOp(f.op)}</span>
                      <Show when={!isNoValueOp(f.op)}>
                        <span class="filter-chip-val mono">{f.value === '' ? '∅' : f.value}</span>
                      </Show>
                      <button class="filter-chip-x" onClick={(e) => { e.stopPropagation(); removeResultFilter(i()); }} title="Remove filter">×</button>
                    </div>
                  </>
                )}
              </For>
              <button class="btn btn-ghost btn-sm" onClick={clearResultFilters}>Clear all</button>
              <Show when={activeFilters().length >= 2}>
                <span class="filter-precedence-hint" title="SQL natural precedence: AND binds tighter than OR. e.g. A AND B OR C means (A AND B) OR C">
                  ⓘ AND binds tighter than OR
                </span>
              </Show>
            </div>
          </Show>

          <div class="query-result-body">
            <Show when={activeResultTab() === 'messages'}>
              <div class="query-messages">
                <Show when={messages.length === 0}>
                  <div class="muted" style="padding:16px;">No messages yet — run a query to see output.</div>
                </Show>
                {/* Newest first: render in reverse so the latest message is on top. */}
                <For each={[...messages].reverse()}>
                  {(m) => <MessageRow m={m} now={nowTick()} warnOpen={warnOpen()} onToggleWarn={toggleWarn} onContextMenu={onMessageContextMenu} />}
                </For>
              </div>
            </Show>
            <Show when={activeResultTab() !== 'messages' && activeTabBody() && isCharting(activeResultTab()) && !isExplainTab(activeTabBody())}>
              <ChartView
                columns={activeTabBody().columns}
                rows={gridRows()}
                title={activeTabBody().label}
              />
            </Show>
            <Show when={activeResultTab() !== 'messages' && activeTabBody() && !isCharting(activeResultTab())}>
              <Switch
                fallback={
                  <Grid
                    columns={activeTabBody().columns}
                    rows={gridRows()}
                    totalRows={(activeTabBody().rows || []).length}
                    showTypes={gridShowTypes()}
                    thousandSep={gridThousandSep()}
                    widthByName={resultWidths()[activeResultTab()]}
                    onResize={handleResultResize}
                    registerAutoFit={registerGridAutoFit}
                    sortKeys={activeSortKeys()}
                    onSortToggle={onResultSortToggle}
                    onHeaderClick={onResultHeaderClick}
                    editable={editResultTab() === activeResultTab()}
                    onRowEdit={handleResultRowEdit}
                    onRowInsert={insertableTab() ? handleResultRowInsert : undefined}
                    onDraftsChange={handleResultDraftsChange}
                    onEditPreview={handleResultEditPreview}
                    registerFlusher={registerResultGridFlusher}
                    registerInsertFlusher={registerResultInsertFlusher}
                    registerRowInserter={registerResultRowInserter}
                    registerDraftClearer={registerResultDraftClearer}
                    registerDirtyClearer={registerResultDirtyClearer}
                    onDirtyChange={setResultDirtyCount}
                    onRowSelectionChange={setResultSelectedRowIdxs}
                    registerRowSelectionClearer={registerResultRowSelectionClearer}
                    getRowKey={resultRowKeyOf}
                    selectable={true}
                  />
                }
              >
                <Match when={activeTabBody().explainJson != null}>
                  <ExplainTree json={activeTabBody().explainJson}
                               analyzeJson={activeTabBody().analyzeJson}
                               analyzeText={activeTabBody().analyzeText} />
                </Match>
                <Match when={activeTabBody().explainText != null}>
                  <pre class="explain-analyze mono">{activeTabBody().explainText}</pre>
                </Match>
              </Switch>
            </Show>

            {/* Inline transaction log while editing a result grid — mirrors the
                table browser's SQL-log pane so the executed statements + pending
                preview are visible without switching to the Messages tab. */}
            <Show when={editResultTab() === activeResultTab()}>
              <div class="query-edit-log">
                <div class="query-edit-log-header">
                  <span>SQL log</span>
                  <span class="muted" style="font-weight:400;margin-left:6px;">
                    ({messages.length} statement{messages.length === 1 ? '' : 's'})
                  </span>
                </div>
                <div class="query-edit-log-body">
                  <Show when={editPreview()}>
                    <div class="query-edit-preview mono" title="The statement this row edit will run when you move off the row or Commit (not yet sent). Click to view in full."
                         onClick={() => setShowPreviewModal(true)}>
                      <span class="query-edit-preview-tag">pending</span>
                      <span class="query-edit-preview-sql">{editPreview()}</span>
                      <span class="query-edit-preview-expand" title="Show full statement">⤢</span>
                    </div>
                  </Show>
                  <For each={pendingInserts()}>
                    {(p) => (
                      <div class="query-edit-preview mono" title="A new row pending — INSERTed on Commit (not yet sent).">
                        <span class="query-edit-preview-tag">pending</span>
                        <span class="query-edit-preview-sql">{p.sql}</span>
                      </div>
                    )}
                  </For>
                  <Show when={messages.length === 0 && !editPreview() && pendingInserts().length === 0}>
                    <div class="muted" style="padding:6px 10px;font-size:12px;">No statements yet — edit a cell to start.</div>
                  </Show>
                  <For each={[...messages].reverse()}>
                    {(m) => <MessageRow m={m} now={nowTick()} warnOpen={warnOpen()} onToggleWarn={toggleWarn} onContextMenu={onMessageContextMenu} />}
                  </For>
                </div>
              </div>
            </Show>
          </div>
        </div>
        </div>
      </div>

      {/* Column filter popover (client-side, over the loaded result rows) */}
      <Show when={filterPopover()}>
        <FilterPopover
          column={filterPopover().column}
          anchorRect={filterPopover().rect}
          initialValue={filterPopover().prefill}
          initialOp={filterPopover().op}
          onSubmit={applyResultFilter}
          onCancel={() => setFilterPopover(null)}
        />
      </Show>

      {/* Editor right-click context menu (rendered at click coords). */}
      <Show when={editorCtxMenu()}>
        <ContextMenu
          x={editorCtxMenu().x}
          y={editorCtxMenu().y}
          items={editorCtxMenu().items}
          onClose={() => setEditorCtxMenu(null)}
        />
      </Show>

      {/* Message-log right-click context menu (Copy message / Copy error). */}
      <Show when={msgCtxMenu()}>
        <ContextMenu
          x={msgCtxMenu().x}
          y={msgCtxMenu().y}
          items={msgCtxMenu().items}
          onClose={() => setMsgCtxMenu(null)}
        />
      </Show>

      {/* Title-prompt: small modal that injects (or replaces) a
          `-- @name: <title>` annotation on the statement at the click. */}
      <Show when={titlePrompt()}>
        <Modal open={true} onClose={() => setTitlePrompt(null)} title="🏷 Result tab title">
          <p class="muted" style="font-size:12px;margin:0 0 12px;">
            <Show
              when={titlePrompt().existingName}
              fallback={<>Inserts <code class="mono">-- @name: …</code> above this statement so its result tab is titled with the name. Visible to anyone reading the SQL — it's just a comment.</>}
            >
              Renames the result tab. Empty value removes the title annotation.
            </Show>
          </p>
          <input
            ref={titleInputRef}
            class="input mono"
            style="width:100%;font-size:14px;"
            placeholder="e.g. Get Manager List"
            value={titlePrompt().draft}
            onInput={(e) => setTitlePrompt({ ...titlePrompt(), draft: e.currentTarget.value })}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); submitTitlePrompt(); }
              else if (e.key === 'Escape') { e.preventDefault(); setTitlePrompt(null); }
            }}
          />
          <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:14px;">
            <button class="btn btn-ghost" onClick={() => setTitlePrompt(null)}>Cancel</button>
            <button class="btn" onClick={submitTitlePrompt}>
              {titlePrompt().existingName
                ? (titlePrompt().draft.trim() === '' ? 'Remove' : 'Rename')
                : 'Add'}
            </button>
          </div>
        </Modal>
      </Show>

      {/* PK-rename confirmation — same pattern as Browser.jsx. Opened from
          handleResultRowEdit when buffered changes touch a PK column; awaits a
          resolve() callback before the UPDATE is sent. */}
      <Show when={pkConfirm()}>
        <PKEditConfirmModal
          open={true}
          connectionId={connId()}
          database={pkConfirm().database}
          table={pkConfirm().table}
          oldPK={pkConfirm().oldPK}
          newPK={pkConfirm().newPK}
          onConfirm={() => pkConfirm().resolve(true)}
          onCancel={() => pkConfirm().resolve(false)}
        />
      </Show>

      {/* Paste-with-template modal — opened from the editor's right-click. */}
      <PasteTemplateModal
        open={!!pasteTemplateView()}
        onClose={() => setPasteTemplateView(null)}
        onPaste={onPasteTemplateConfirm}
      />

      {/* Query profiler — opened from the Explain menu's "Profile" item. */}
      <Show when={profileSql() != null}>
        <QueryProfiler
          connectionId={connId()}
          database={currentDb()}
          sql={profileSql()}
          onClose={() => setProfileSql(null)}
        />
      </Show>

      {/* Find Usages modal — search across saved queries, scraps, history,
          forms, routines, triggers, views. "Open in tab" reuses the existing
          onOpenQueryWith path so the host can keep its tab-state shape. */}
      <EditorHistoryModal
        open={historyOpen()}
        tabKey={props.tabId}
        currentSql={sqlText()}
        onRestore={(sql) => setSqlText(sql)}
        onClose={() => setHistoryOpen(false)}
      />

      <FindUsagesModal
        open={findOpen()}
        onClose={() => setFindOpen(false)}
        connectionId={connId()}
        currentDatabase={currentDb()}
        initialToken={findInitial()}
        onOpenSaved={async (id) => {
          // Saved queries: fetch the row, open it in a new tab. The saved-query
          // sidebar already does this — we mimic by fetching and using onOpenQueryWith.
          try {
            const r = await api.get(`/api/saved-queries/${id}`);
            const q = r?.savedQuery || r;
            props.onOpenQueryWith?.(q?.sourceDatabase || currentDb(), q?.sqlText || '', false);
          } catch (e) {
            toast.error('Could not open saved query: ' + e.message);
          }
        }}
        onOpenScrap={async (id) => {
          // Scraps already have a list-fetch + restore path; simplest is to
          // re-fetch the row via the list endpoint and open its sqlText.
          try {
            const r = await api.get('/api/me/scraps');
            const sc = (r?.scraps || []).find((x) => x.id === id);
            if (sc) props.onOpenQueryWith?.(sc.database || currentDb(), sc.sqlText || '', false);
            else toast.error('Scrap not found (it may have been deleted)');
          } catch (e) {
            toast.error('Could not open scrap: ' + e.message);
          }
        }}
        onOpenHistory={(sql) => {
          props.onOpenQueryWith?.(currentDb(), sql || '', false);
        }}
        onOpenSql={(sql, label) => {
          // Generic destination — open the body in a new tab. label is just a
          // hint for the host; if the host's tab labelling isn't customizable
          // this falls back to the default "Untitled".
          props.onOpenQueryWith?.(currentDb(), sql || '', false, label);
        }}
      />

      {/* Safe-update guard — covers every reason a write was blocked client-side
          (no WHERE, no indexed-key WHERE under Lax-Key, table unknown to cache).
          The message + button label adapt to the mix; multiple reasons can show
          together if a batch run has different problems. */}
      <Show when={unsafePrompt()}>
        {(() => {
          // Each item: { kind, sql, reason: 'no-where'|'no-key-where'|'no-key-info'|'unknown-table', table?, keys? }
          const items = unsafePrompt().items;
          const anyNoWhere = items.some((it) => it.reason === 'no-where' || !it.reason);
          const anyKey     = items.some((it) => it.reason === 'no-key-where');
          const anyInfo    = items.some((it) => it.reason === 'no-key-info' || it.reason === 'unknown-table');
          const title = anyNoWhere
            ? '⚠ Affects every row'
            : anyKey
              ? '⚠ WHERE doesn’t reference an indexed column'
              : '⚠ Can’t verify the WHERE is on an indexed column';
          const reasonLabel = (r) => (
            r === 'no-key-where'  ? 'no indexed column' :
            r === 'no-key-info'   ? 'no schema info'    :
            r === 'unknown-table' ? 'unknown table'     :
            'no WHERE'
          );
          return (
            <div class="modal-backdrop" onClick={() => resolveUnsafe(false)}>
              <div class="modal-card" style="width:min(640px,92vw);" onClick={(e) => e.stopPropagation()}>
                <h3 style="color:var(--danger,#ef4444);">{title}</h3>
                <p class="muted" style="font-size:12px;margin:0 0 12px;">
                  <Show when={anyNoWhere}>
                    A bare <code>UPDATE</code>/<code>DELETE</code> would affect <strong>every row</strong>.
                  </Show>
                  <Show when={anyKey}>
                    {' '}Under <em>Lax — key column</em>, the WHERE must reference a column that participates in any index (PK, unique, or secondary).
                  </Show>
                  <Show when={anyInfo}>
                    {' '}This table isn't in the cached schema (or the cache pre-dates the index field) — Ninocat can't verify locally. Click <strong>⟳ Sync schema</strong> in Options if the table exists.
                  </Show>
                </p>
                <div class="unsafe-list">
                  <For each={items}>
                    {(it) => (
                      <div class="unsafe-item">
                        <span class={`unsafe-kind unsafe-${it.kind.toLowerCase()}`}>{it.kind}</span>
                        <span class="muted" style="font-size:11px;margin-right:6px;">{reasonLabel(it.reason)}{it.table ? ` · ${it.table}` : ''}</span>
                        <code class="mono">{it.sql.length > 200 ? it.sql.slice(0, 200) + '…' : it.sql}</code>
                        <Show when={it.reason === 'no-key-where' && it.keys?.length}>
                          <div class="muted" style="font-size:11px;margin-top:4px;">Indexed columns on <span class="mono">{it.table}</span>: <span class="mono">{it.keys.join(', ')}</span></div>
                        </Show>
                      </div>
                    )}
                  </For>
                </div>
                <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:16px;">
                  <button class="btn btn-ghost" onClick={() => resolveUnsafe(false)} autofocus>Cancel</button>
                  <button class="btn btn-danger" onClick={() => resolveUnsafe(true)}>Run anyway</button>
                </div>
              </div>
            </div>
          );
        })()}
      </Show>

      {/* Implicit-commit guard — shown when a statement would commit the open tx. */}
      <Show when={commitPrompt()}>
        <div class="modal-backdrop" onClick={() => resolveCommit(false)}>
          <div class="modal-card" style="width:min(560px,92vw);" onClick={(e) => e.stopPropagation()}>
            <h3 style="color:var(--warn,#f59e0b);">⚠ This will commit your open transaction</h3>
            <p class="muted" style="font-size:12px;margin:0 0 12px;">
              You have an <strong>open transaction</strong>. MySQL automatically commits it before
              {' '}{commitPrompt().items.length === 1 ? 'this statement' : 'these statements'}, and the
              {' '}commit <strong>cannot be rolled back</strong>:
            </p>
            <div class="unsafe-list">
              <For each={commitPrompt().items}>
                {(it) => (
                  <div class="unsafe-item">
                    <span class="commit-kind">{it.kind}</span>
                    <code class="mono">{it.sql.length > 200 ? it.sql.slice(0, 200) + '…' : it.sql}</code>
                  </div>
                )}
              </For>
            </div>
            <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:16px;">
              <button class="btn btn-ghost" onClick={() => resolveCommit(false)} autofocus>Cancel</button>
              <button class="btn btn-danger" onClick={() => resolveCommit(true)}>Commit &amp; run</button>
            </div>
          </div>
        </div>
      </Show>

      {/* Parameter prompt — shown when a run contains :name parameters. */}
      <Show when={paramPrompt()}>
        <div class="modal-backdrop" onClick={cancelParams}>
          <div class="modal-card" style="width:min(520px,92vw);" onClick={(e) => e.stopPropagation()}>
            <h3>Query parameters</h3>
            <p class="muted" style="font-size:12px;margin:0 0 14px;">
              Enter a value for each parameter. Values are <strong>bound safely</strong> (sent as
              parameters, never pasted into the SQL).
            </p>
            <For each={paramPrompt().params}>
              {(p, i) => (
                <div class="param-row">
                  <label class="field-label">
                    {p.name}
                    <Show when={p.type}><span class="muted mono"> #{p.type}#</span></Show>
                  </label>
                  <div class="param-input-row">
                    <Show
                      when={p.type === 'bool'}
                      fallback={
                        <input
                          class="input"
                          type={paramInputType(p.type)}
                          value={paramPrompt().values[p.name]}
                          disabled={paramPrompt().nulls[p.name]}
                          autofocus={i() === 0}
                          onInput={(e) => setParamValue(p.name, e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') submitParams(); }}
                        />
                      }
                    >
                      <select
                        class="input"
                        value={paramPrompt().values[p.name] || '1'}
                        disabled={paramPrompt().nulls[p.name]}
                        onChange={(e) => setParamValue(p.name, e.target.value)}
                      >
                        <option value="1">True (1)</option>
                        <option value="0">False (0)</option>
                      </select>
                    </Show>
                    <label class="checkbox-label param-null" title="Bind SQL NULL for this parameter">
                      <input
                        type="checkbox"
                        checked={paramPrompt().nulls[p.name]}
                        onChange={(e) => setParamNull(p.name, e.target.checked)}
                      />
                      NULL
                    </label>
                  </div>
                </div>
              )}
            </For>
            <Show when={props.onOpenQueryWith}>
              <p class="muted" style="font-size:11.5px;margin:14px 0 0;">
                “Fill into new tab” writes the values as <strong>literals</strong> into a fresh, self-contained
                query and runs it in a new tab — handy to keep or share the exact query.
              </p>
            </Show>
            <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:16px;flex-wrap:wrap;">
              <button class="btn btn-ghost" onClick={cancelParams}>Cancel</button>
              <Show when={props.onOpenQueryWith}>
                <button class="btn btn-ghost" onClick={fillParamsIntoNewTab}
                        title="Open a new query tab with the values filled in as literals, and run it there">
                  ↗ Fill into new tab &amp; run
                </button>
              </Show>
              <button class="btn" onClick={submitParams}>▶ Run</button>
            </div>
          </div>
        </div>
      </Show>

      {/* Save / Edit dialog */}
      <Show when={savingDlg()}>
        <div class="modal-backdrop" onClick={closeSaveDialog}>
          <div class="modal-card" style="width:min(640px,92vw);" onClick={(e) => e.stopPropagation()}>
            <h3>{savingDlg().editingId ? 'Edit saved query' : 'Save query'}</h3>
            <label class="field-label">Title</label>
            <input class="input" value={savingDlg().title}
                   onInput={(e) => setSavingDlg({ ...savingDlg(), title: e.target.value })}
                   placeholder="e.g. Top customers by revenue" autofocus />
            <label class="field-label" style="margin-top:10px;">Description (optional)</label>
            <textarea class="input" rows="3" value={savingDlg().description}
                      onInput={(e) => setSavingDlg({ ...savingDlg(), description: e.target.value })}
                      placeholder="What does this query do?" />
            <label class="field-label" style="margin-top:10px;">Source database (informational)</label>
            <input class="input mono" value={savingDlg().sourceDatabase}
                   onInput={(e) => setSavingDlg({ ...savingDlg(), sourceDatabase: e.target.value })}
                   placeholder="e.g. prd_tamanpangan" />

            {/* Tag-scoped sharing — visible (read-only) to users carrying a matching tag. */}
            <label class="checkbox-label" style="margin-top:12px;">
              <input type="checkbox" checked={savingDlg().shared}
                     onChange={(e) => setSavingDlg({ ...savingDlg(), shared: e.currentTarget.checked })} />
              <span>Share with tagged users</span>
            </label>
            <Show when={savingDlg().shared}>
              <div class="muted" style="font-size:12px;margin:4px 0;">
                Read-only for users who have any of these tags:
              </div>
              <Show when={(tagOptions() || []).length}
                    fallback={<div class="muted" style="font-size:12px;">No tags exist yet — an admin assigns tags to users on the Users page.</div>}>
                <div class="sq-tag-picker">
                  <For each={tagOptions()}>
                    {(t) => (
                      <label class="checkbox-label">
                        <input type="checkbox" checked={(savingDlg().shareTags || []).includes(t)} onChange={() => toggleShareTag(t)} />
                        <span>{t}</span>
                      </label>
                    )}
                  </For>
                </div>
              </Show>
            </Show>

            <label class="field-label" style="margin-top:10px;">Query content</label>
            <div class="saved-query-editor">
              <CodeMirrorSQL
                value={savingDlg().sqlText}
                onInput={(v) => setSavingDlg({ ...savingDlg(), sqlText: v })}
                schema={sqlSchema}
                height="220px"
                placeholder="SELECT …"
              />
            </div>
            <div style="display:flex;gap:8px;margin-top:14px;justify-content:flex-end;">
              <button class="btn btn-ghost" onClick={closeSaveDialog}>Cancel</button>
              <button class="btn" onClick={commitSave}>Save</button>
            </div>
          </div>
        </div>
      </Show>

      {/* Pending-statement full view (the one-line preview bar opens this) */}
      <Show when={showPreviewModal() && editPreview()}>
        <div class="modal-backdrop" onClick={() => setShowPreviewModal(false)}>
          <div class="modal-card" style="width:min(760px,94vw);" onClick={(e) => e.stopPropagation()}>
            <h3>Pending statement <span class="muted" style="font-size:12px;font-weight:400;">— not yet sent</span></h3>
            <pre class="long-text-view mono">{editPreview()}</pre>
            <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:12px;">
              <button class="btn btn-ghost" onClick={() => setShowPreviewModal(false)}>Close</button>
            </div>
          </div>
        </div>
      </Show>

      {/* Load: append-or-replace prompt (only when the editor isn't empty) */}
      <Show when={loadPrompt()}>
        {/* Backdrop click is inert — Append/Replace is a deliberate choice, so an
            accidental outside-click shouldn't silently cancel it (use Cancel). */}
        <div class="modal-backdrop">
          <div class="modal-card" style="width:min(440px,92vw);">
            <h3>Load {loadPrompt().label}</h3>
            <p class="muted" style="margin:6px 0 14px;">
              The editor already has content. Append the loaded query below it, or replace everything?
            </p>
            <div style="display:flex;gap:8px;justify-content:flex-end;">
              <button class="btn btn-ghost" onClick={() => setLoadPrompt(null)}>Cancel</button>
              <button class="btn" onClick={() => {
                applyLoad(loadPrompt().sql, 'append'); logInfo(`Appended ${loadPrompt().label}`); setLoadPrompt(null);
              }}>Append</button>
              <button class="btn btn-danger" onClick={() => {
                applyLoad(loadPrompt().sql, 'replace'); logInfo(`Replaced editor with ${loadPrompt().label}`); setLoadPrompt(null);
              }}>Replace</button>
            </div>
          </div>
        </div>
      </Show>

      {/* Ctrl+Shift+E — expand a table's columns at the cursor */}
      <ColumnExpandModal
        open={!!expandReq()}
        request={expandReq()}
        connId={connId}
        currentDb={currentDb}
        onClose={() => setExpandReq(null)}
      />

      {/* 📖 DSL — query-DSL / editor-extras help */}
      <QueryDslHelp open={dslHelpOpen()} onClose={() => setDslHelpOpen(false)} />
    </div>
  );
}

// First non-blank line of a statement, truncated — the history list preview.
function firstLine(s) {
  const line = (s || '').split('\n').find((l) => l.trim()) || '';
  return line.length > 80 ? line.slice(0, 80) + '…' : line;
}

